// Leadsv2.jsx
// V3 com barra de filtros restaurada na ordem da V2
// Mantendo:
// - bootstrap mais leve
// - lazy load de dados auxiliares
// - virtualização simples da tabela
// - refresh centralizado da lista

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useTransition,
} from "react";
import { useTable, useFilters, useSortBy } from "react-table";
import {
  PlusCircle,
  Trash,
  Save,
  CheckCircle,
  CircleDollarSign,
  XCircle,
  RotateCcw,
  AlertCircle,
  CornerDownRight,
  CalendarCheck,
  PhoneCall,
  Target,
  HandCoins,
  Search,
  StickyNote,
} from "lucide-react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Leads.css";

import logoCapta from "../assets/logo-capta.png";
import TarefaModal from "../Components/TarefaModal";
import InteracoesModal from "../Components/InteracoesModal";
import NotificacaoTarefa from "../Components/NotificacaoTarefa";
import { api } from "../api";
import DropdownAcoes from "../Components/DropdownAcoes";
import getCorBolinhaStatusLead from "../utils/getCorBolinhaStatusLead";
import ModalLegendaBolinhas from "../Components/ModalLegendaBolinhas";
import RecebimentoModal from "../Components/RecebimentoModal";
import LogsModalLead from "../Components/LogsModalLead";
import CountUp from "react-countup";
import CentralDisparosLeadSpeed from "../Components/CentralDisparosLeadSpeed";
import AgendamentosModal from "../Components/AgendamentosModal";
import FiltroDataDinamico from "../Components/FiltroDataDinamico";
import LeadsExportExcel from "../Components/LeadsExportExcel";
import LeadsWhatsApp from "../Components/LeadsWhatsApp";
import { FaWhatsapp } from "react-icons/fa";
import WhatsappIconWithBadge from "../Components/WhatsappIconWithBadge";
import { useEmpresa } from "../context/EmpresaContext";
import FormLead from "./FormLead";
import CadeadoLead from "../Components/CadeadoLeads";
import TransferenciaLeadModal from "../Components/TransferenciaLeadModal";
import {
  initLeadsTelemetry,
  useRenderCounter,
  appMarkStart,
  appMarkEnd,
} from "../utils/leadsTelemetryPro";
import { getApiBase } from "../utils/apiBase";
import CaptaChatWidget from "../utils/CaptaChatWidget";
import SorrisoPlanejado from "./SorrisoPlanejado";

if (typeof window !== "undefined" && !window.CAPTA_MODE) {
  window.CAPTA_MODE = "turbo-perf-lite";
}

const IS_TURBO =
  typeof window !== "undefined" && window.CAPTA_MODE === "turbo-perf-lite";

const statusNegociacaoOpcoes = [
  {
    label: "Fechado",
    value: "Fechado",
    icon: <CheckCircle className="w-4 h-4 text-green-600" />,
  },
  {
    label: "Fechado Parcial",
    value: "Fechado Parcial",
    icon: <CircleDollarSign className="w-4 h-4 text-emerald-500" />,
  },
  {
    label: "Negociando",
    value: "Negociando",
    icon: <RotateCcw className="w-4 h-4 text-yellow-500" />,
  },
  {
    label: "Pendente",
    value: "Pendente",
    icon: <AlertCircle className="w-4 h-4 text-gray-500" />,
  },
  {
    label: "Prospectando",
    value: "Prospectando",
    icon: <Search className="w-4 h-4 text-sky-500" />,
  },
  {
    label: "Migrou Concorrência",
    value: "Migrou Concorrência",
    icon: <CornerDownRight className="w-4 h-4 text-orange-500" />,
  },
  {
    label: "Perdido",
    value: "Perdido",
    icon: <XCircle className="w-4 h-4 text-red-500" />,
  },
];

const statusAgendamentoOpcoes = [
  { label: "Agendado", value: "Agendado", icon: "📅" },
  { label: "Reagendado", value: "Reagendado", icon: "🔁" },
  { label: "Confirmado", value: "Confirmado", icon: "✅" },
  { label: "Compareceu", value: "Compareceu", icon: "🟢" },
  { label: "Faltou", value: "Faltou", icon: "🔴" },
  { label: "Pendente", value: "Pendente", icon: "🟡" },
  { label: "Cancelado", value: "Cancelado", icon: "❌" },
  { label: "Sem Agendamento", value: "__sem_agendamento__", icon: "📭" },
];

const STATUS_INATIVAS_NORM = new Set(["perdido", "migrou concorrencia"]);
const STATUS_TAREFA_CONCLUIDA_NORM = new Set([
  "concluida",
  "concluido",
  "finalizada",
  "finalizado",
  "feito",
  "feita",
]);
const STATUS_TAREFA_POS_VENDA_NORM = new Set([
  "pos venda",
  "pos-venda",
  "pos_venda",
  "posvenda",
]);
const STATUS_NEGOCIACAO_PRIORIDADE = [
  "Fechado",
  "Fechado Parcial",
  "Negociando",
  "Pendente",
  "Prospectando",
  "Migrou Concorrência",
  "Perdido",
];
const STATUS_NEGOCIACAO_RECEBIMENTO_NORM = new Set([
  "fechado",
  "fechado parcial",
]);
const STATUS_AGENDAMENTO_ATIVO_NORM = new Set([
  "agendado",
  "reagendado",
  "confirmado",
]);

const VIRTUAL_ROW_HEIGHT = 46;
const VIRTUAL_OVERSCAN = 12;
const VIRTUAL_MIN_ROWS = 80;

function DefaultColumnFilter({ column: { filterValue, setFilter } }) {
  return (
    <input
      value={filterValue || ""}
      onChange={(e) => setFilter(e.target.value)}
      placeholder="Filtrar..."
      className="w-full px-2 py-1 border border-gray-300 rounded-md text-sm focus:outline-none"
    />
  );
}

function soData(x) {
  if (!x) return null;

  if (x instanceof Date && !isNaN(x)) {
    const y = x.getFullYear();
    const m = String(x.getMonth() + 1).padStart(2, "0");
    const d = String(x.getDate()).padStart(2, "0");
    return Number(`${y}${m}${d}`);
  }

  if (typeof x === "string" && x.length >= 10) {
    const ymd = x.slice(0, 10).trim();
    if (/^\d{4}-\d{2}-\d{2}$/.test(ymd)) return Number(ymd.replace(/-/g, ""));
  }

  return null;
}

function useDebouncedValue(value, delay = 200) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);

  return debounced;
}

function norm(s) {
  return (s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .trim()
    .toLowerCase();
}

function pad2(n) {
  return String(n).padStart(2, "0");
}

function addDaysLocal(ymd, days) {
  if (!ymd) return ymd;
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  dt.setDate(dt.getDate() + days);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function hojeYmdLocal() {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${pad2(hoje.getMonth() + 1)}-${pad2(hoje.getDate())}`;
}

function toMoneyNum(v) {
  if (v == null || v === "") return 0;
  if (typeof v === "number" && Number.isFinite(v)) return v;

  const s = String(v)
    .replace(/\s|R\$\s?/g, "")
    .trim();

  if (/^[\d.]+,\d{2}$/.test(s)) {
    return Number(s.replace(/\./g, "").replace(",", "."));
  }

  const n = Number(s.replace(/[^\d.-]/g, ""));
  return Number.isFinite(n) ? n : 0;
}

function moeda(v) {
  return Number(v || 0).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
  });
}

function getValorRecebimentoLead(recebimento = {}) {
  // Usa somente campos que representam pagamento/recebimento real.
  // Evita campos genéricos como total/valor_total, que em alguns retornos
  // podem representar orçamento/contrato e causar falso positivo no filtro.
  return toMoneyNum(
    recebimento?.valor_recebido ??
      recebimento?.valor_recebimento ??
      recebimento?.valor_pago ??
      recebimento?.valor_parcela ??
      recebimento?.valor ??
      recebimento?.recebido ??
      recebimento?.valorRecebido ??
      0,
  );
}

function somarRecebimentosLead(recebimentos = []) {
  return (Array.isArray(recebimentos) ? recebimentos : []).reduce(
    (total, item) => total + getValorRecebimentoLead(item),
    0,
  );
}

function isLeadOrcamentoSemOportunidadeOuOportunidadeMenorRecebido(
  lead = {},
  recebimentos = [],
) {
  const totalOrcamento = toMoneyNum(lead?.total_orcamento);
  const totalFechado = toMoneyNum(lead?.total_contratado);
  const margemCentavos = 0.01;

  // Regra solicitada:
  // trazer somente leads onde o TOTAL/ORÇAMENTO está zerado
  // OU onde o TOTAL/ORÇAMENTO é menor que o FECHADO/OPORTUNIDADE.
  // Não usa recebimentos financeiros nesta regra.
  const totalZerado = totalOrcamento <= margemCentavos;
  const totalMenorQueFechado =
    totalFechado > margemCentavos &&
    totalOrcamento + margemCentavos < totalFechado;

  return totalZerado || totalMenorQueFechado;
}

function normalizarTelefoneLead(t) {
  return String(t || "").replace(/\D/g, "");
}

function normalizarNomeLead(s) {
  return String(s || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\b(da|de|do|das|dos|e)\b/g, " ")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function nomeTokensLead(s) {
  return normalizarNomeLead(s)
    .split(" ")
    .filter((x) => x && x.length > 1);
}

const ID_PROCEDIMENTO_MANUTENCAO_APARELHO = 9;

function isLeadManutencaoAparelho(lead = {}) {
  const idProcedimento = Number(lead?.id_procedimento || 0);
  const nomeProcedimento = String(
    lead?.procedimento ||
      lead?.nome_procedimento ||
      lead?.procedimento_nome ||
      lead?.nomeProcedimento ||
      "",
  )
    .trim()
    .toLowerCase();

  const observacao = String(lead?.observacao_paciente || "")
    .trim()
    .toLowerCase();

  return (
    idProcedimento === ID_PROCEDIMENTO_MANUTENCAO_APARELHO ||
    nomeProcedimento.includes("manutenção") ||
    nomeProcedimento.includes("manutencao") ||
    observacao.includes("recorrência ortodontia") ||
    observacao.includes("recorrencia ortodontia") ||
    observacao.includes("carteira: recorrência") ||
    observacao.includes("carteira: recorrencia")
  );
}

function diceSimilarityLead(a, b) {
  const sa = normalizarNomeLead(a);
  const sb = normalizarNomeLead(b);

  if (!sa || !sb) return 0;
  if (sa === sb) return 1;

  const bigrams = (str) => {
    const compact = str.replace(/\s+/g, "");
    if (compact.length < 2) return [compact];
    const arr = [];
    for (let i = 0; i < compact.length - 1; i += 1) {
      arr.push(compact.slice(i, i + 2));
    }
    return arr;
  };

  const aPairs = bigrams(sa);
  const bPairs = bigrams(sb);
  const used = new Array(bPairs.length).fill(false);
  let hits = 0;

  for (const pair of aPairs) {
    const idx = bPairs.findIndex((bp, i) => !used[i] && bp === pair);
    if (idx >= 0) {
      used[idx] = true;
      hits += 1;
    }
  }

  return (2 * hits) / (aPairs.length + bPairs.length || 1);
}

function nomesParecidosLead(a, b) {
  const na = normalizarNomeLead(a);
  const nb = normalizarNomeLead(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  if (na.includes(nb) || nb.includes(na)) return true;

  const ta = nomeTokensLead(a);
  const tb = nomeTokensLead(b);
  const primeiroIgual = ta[0] && tb[0] && ta[0] === tb[0];
  const intersecao = ta.filter((x) => tb.includes(x));
  const ultimoA = ta[ta.length - 1];
  const ultimoB = tb[tb.length - 1];
  const ultimoIgual = ultimoA && ultimoB && ultimoA === ultimoB;

  return (
    diceSimilarityLead(a, b) >= 0.72 ||
    (primeiroIgual && (ultimoIgual || intersecao.length >= 2))
  );
}

function isLeadInativaOuPerdida(lead) {
  const st = norm(lead?.status_negociacao);
  return (
    Number(lead?.inativo) === 1 ||
    st === "perdido" ||
    st === "migrou concorrencia"
  );
}

function getPrimeiroNomeById(id, usuarios) {
  const user = (usuarios || []).find((u) => String(u.id) === String(id));
  return user ? user.nome.split(" ")[0] : "";
}

function getDomWeight() {
  const el = document.getElementById("tabela-leads-v3");
  if (!el) return { rows: 0, cells: 0 };

  const rows =
    el.querySelectorAll("tr").length ||
    el.querySelectorAll("[role='row']").length;
  const cells =
    el.querySelectorAll("td,th").length ||
    el.querySelectorAll("[role='cell']").length;

  return { rows, cells };
}

async function postPerfLite({ empresaId, userId, app_ms }) {
  const url = `${getApiBase()}/telemetria/leads`;
  const clientVersion =
    (typeof window !== "undefined" && window.CAPTA_MODE) || "stable";

  const body = {
    empresa_id: empresaId ?? null,
    user_id: userId ?? null,
    page: "leadsv3",
    client_version: `${clientVersion}-v3`,
    sent_at: new Date().toISOString(),
    dom: getDomWeight(),
    app_ms,
  };

  try {
    await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
      credentials: "include",
      keepalive: true,
    });
  } catch {}
}

const FormLeadWrapper = React.memo(function FormLeadWrapper(props) {
  return <FormLead {...props} />;
});

const WhatsAppCell = React.memo(function WhatsAppCell({
  lead,
  badgesWhatsapp,
  onOpen,
}) {
  const badgeData = badgesWhatsapp?.[lead?.id] || {};
  const badgeCount = badgeData.count || 0;
  const badgeStatus = badgeData.status || null;

  return (
    <button
      onClick={() => onOpen(lead)}
      style={{
        background: "none",
        border: "none",
        cursor: "pointer",
        position: "relative",
        padding: 0,
      }}
      title="Abrir chat do WhatsApp"
      type="button"
    >
      <WhatsappIconWithBadge
        showBadge={!!badgeCount}
        badgeCount={badgeCount}
        badgeColor={badgeStatus}
      />
    </button>
  );
});

const InlineValorMinimoCell = React.memo(function InlineValorMinimoCell({
  lead,
  onSave,
}) {
  const [editando, setEditando] = useState(false);
  const [valorTexto, setValorTexto] = useState("");
  const [salvando, setSalvando] = useState(false);

  const abrirEdicao = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();

    const valorAtual = lead?.valor_minimo;
    const valorFormatado =
      valorAtual === null || valorAtual === undefined || valorAtual === ""
        ? ""
        : toMoneyNum(valorAtual).toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });

    setValorTexto(valorFormatado);
    setEditando(true);
  };

  const cancelar = (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (salvando) return;

    setValorTexto("");
    setEditando(false);
  };

  const salvar = async (event) => {
    event?.preventDefault?.();
    event?.stopPropagation?.();
    if (salvando) return;

    const texto = String(valorTexto || "").trim();
    const valorMinimo = texto === "" ? null : toMoneyNum(texto);

    if (texto !== "" && valorMinimo < 0) {
      toast.warning("Informe um valor mínimo válido.");
      return;
    }

    try {
      setSalvando(true);
      const salvou = await onSave(lead, valorMinimo);

      if (salvou) {
        setValorTexto("");
        setEditando(false);
      }
    } finally {
      setSalvando(false);
    }
  };

  if (editando) {
    return (
      <div className="flex items-center gap-1 min-w-[150px]">
        <input
          autoFocus
          type="text"
          inputMode="decimal"
          value={valorTexto}
          onChange={(event) => setValorTexto(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") salvar(event);
            if (event.key === "Escape") cancelar(event);
          }}
          onClick={(event) => event.stopPropagation()}
          placeholder="8.500,00"
          className="w-[105px] border-b border-purple-300 bg-transparent px-1 py-1 text-sm font-semibold text-purple-900 outline-none focus:border-purple-600"
          aria-label="Valor mínimo autorizado"
        />
        <button
          type="button"
          onClick={salvar}
          disabled={salvando}
          title="Salvar valor mínimo"
          className="px-1 text-xs font-semibold text-purple-700 transition hover:text-purple-900 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {salvando ? "..." : "OK"}
        </button>
        <button
          type="button"
          onClick={cancelar}
          disabled={salvando}
          title="Cancelar alteração"
          className="px-1 text-xs text-slate-400 transition hover:text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
        >
          ×
        </button>
      </div>
    );
  }

  const temValor =
    lead?.valor_minimo !== null &&
    lead?.valor_minimo !== undefined &&
    lead?.valor_minimo !== "";

  return (
    <button
      type="button"
      onClick={abrirEdicao}
      title={
        temValor
          ? "Clique para alterar o valor mínimo autorizado"
          : "Clique para lançar o valor mínimo autorizado"
      }
      className={`group inline-flex items-center gap-1 border-b border-transparent py-1 text-sm font-semibold transition whitespace-nowrap ${
        temValor
          ? "text-purple-700 hover:border-purple-200 hover:text-purple-900"
          : "text-slate-400 hover:border-purple-200 hover:text-purple-700"
      }`}
    >
      <span>{temValor ? moeda(lead.valor_minimo) : "Mínimo"}</span>
      <span className="text-[11px] font-normal opacity-0 transition-opacity group-hover:opacity-70">
        ✎
      </span>
    </button>
  );
});

const StatusBolinhaCell = React.memo(function StatusBolinhaCell({
  lead,
  tarefasPorLead,
  interacoesPorLead,
}) {
  try {
    const tarefasDaLead = tarefasPorLead.get(String(lead.id)) || [];
    const interacoesDaLead = interacoesPorLead.get(String(lead.id)) || [];

    if (tarefasDaLead.length === 0) {
      return (
        <div
          className="sticky left-0 z-10 bg-white flex justify-center pt-[6px] text-xl"
          title="Lead sem tarefa"
        >
          <span style={{ color: "#FFA500" }}>⚠️</span>
        </div>
      );
    }

    const status = getCorBolinhaStatusLead(
      lead,
      tarefasDaLead,
      interacoesDaLead,
    );

    return (
      <div
        className="sticky left-0 z-10 bg-white flex justify-center pt-[6px] text-xl"
        title="Status calculado automaticamente"
      >
        {status?.emoji || "⚪"}
      </div>
    );
  } catch (err) {
    console.error("❌ Erro ao calcular bolinha da lead:", err);
    return (
      <div className="sticky left-0 z-10 bg-white flex justify-center pt-[6px] text-xl">
        ⚪
      </div>
    );
  }
});


const FiltroStatusAgendamentoMulti = React.memo(
  function FiltroStatusAgendamentoMulti({ value = [], onChange, opcoes = [] }) {
    const [aberto, setAberto] = useState(false);
    const containerRef = useRef(null);

    const selecionados = Array.isArray(value) ? value : [];

    const labelAtual = useMemo(() => {
      if (selecionados.length === 0) return "Status";
      if (selecionados.length === 1) {
        const opcao = opcoes.find((item) => item.value === selecionados[0]);
        return opcao?.label || selecionados[0];
      }
      return `${selecionados.length} selecionados`;
    }, [selecionados, opcoes]);

    useEffect(() => {
      if (!aberto) return;

      const handlePointerDown = (event) => {
        if (!containerRef.current?.contains(event.target)) setAberto(false);
      };
      const handleKeyDown = (event) => {
        if (event.key === "Escape") setAberto(false);
      };

      document.addEventListener("mousedown", handlePointerDown);
      document.addEventListener("keydown", handleKeyDown);
      return () => {
        document.removeEventListener("mousedown", handlePointerDown);
        document.removeEventListener("keydown", handleKeyDown);
      };
    }, [aberto]);

    const alternar = useCallback(
      (status) => {
        const existe = selecionados.includes(status);
        onChange?.(
          existe
            ? selecionados.filter((item) => item !== status)
            : [...selecionados, status],
        );
      },
      [selecionados, onChange],
    );

    return (
      <div ref={containerRef} className="relative w-full">
        <span className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-400 text-[11px] pointer-events-none z-[1]">
          📍
        </span>
        <button
          type="button"
          onClick={() => setAberto((valor) => !valor)}
          aria-haspopup="listbox"
          aria-expanded={aberto}
          title={
            selecionados.length > 0
              ? selecionados
                  .map((valor) =>
                    opcoes.find((item) => item.value === valor)?.label || valor,
                  )
                  .join(" + ")
              : "Status do agendamento"
          }
          className="pl-7 pr-7 h-8 rounded-xl border-2 border-blue-400 text-blue-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-pink-200 transition-all duration-200 w-full text-[11px] bg-white text-left truncate relative"
        >
          <span className="block truncate">{labelAtual}</span>
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-blue-500 pointer-events-none">
            {aberto ? "▲" : "▼"}
          </span>
        </button>

        {aberto && (
          <div
            role="listbox"
            aria-multiselectable="true"
            className="absolute left-0 top-[36px] z-[999] min-w-[230px] rounded-xl border-2 border-blue-200 bg-white p-2 shadow-2xl"
          >
            <div className="max-h-[300px] overflow-y-auto">
              {opcoes.map((opcao) => {
                const marcado = selecionados.includes(opcao.value);
                return (
                  <label
                    key={opcao.value}
                    className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-[11px] transition ${
                      marcado
                        ? "bg-blue-50 text-blue-800"
                        : "text-slate-700 hover:bg-slate-50"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={marcado}
                      onChange={() => alternar(opcao.value)}
                      className="h-3.5 w-3.5 accent-blue-600"
                    />
                    <span className="shrink-0">{opcao.icon}</span>
                    <span className="truncate font-semibold">{opcao.label}</span>
                  </label>
                );
              })}
            </div>

            {selecionados.length > 0 && (
              <div className="mt-2 border-t border-slate-100 pt-2">
                <button
                  type="button"
                  onClick={() => onChange?.([])}
                  className="w-full rounded-lg px-2 py-1.5 text-[10px] font-bold text-red-600 hover:bg-red-50"
                >
                  Limpar seleção
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

const FiltroNegociacaoMulti = React.memo(function FiltroNegociacaoMulti({
  value = [],
  onChange,
  opcoes = [],
}) {
  const [aberto, setAberto] = useState(false);
  const containerRef = useRef(null);

  const selecionados = Array.isArray(value) ? value : [];

  const labelAtual = useMemo(() => {
    if (selecionados.length === 0) return "Negociação";
    if (selecionados.length === 1) return selecionados[0];
    return `${selecionados.length} selecionados`;
  }, [selecionados]);

  useEffect(() => {
    if (!aberto) return;

    const handlePointerDown = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setAberto(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") setAberto(false);
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [aberto]);

  const alternar = useCallback(
    (status) => {
      const existe = selecionados.includes(status);
      const proximo = existe
        ? selecionados.filter((item) => item !== status)
        : [...selecionados, status];

      onChange?.(proximo);
    },
    [selecionados, onChange],
  );

  return (
    <div ref={containerRef} className="relative w-full">
      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-pink-400 text-[11px] pointer-events-none z-[1]">
        💼
      </span>

      <button
        type="button"
        onClick={() => setAberto((valor) => !valor)}
        aria-haspopup="listbox"
        aria-expanded={aberto}
        title={
          selecionados.length > 1 ? selecionados.join(" + ") : labelAtual
        }
        className="pl-7 pr-7 h-8 rounded-xl border-2 border-pink-400 text-pink-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all duration-200 w-full text-[11px] bg-white text-left truncate relative"
      >
        <span className="block truncate">{labelAtual}</span>
        <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-pink-500 pointer-events-none">
          {aberto ? "▲" : "▼"}
        </span>
      </button>

      {aberto && (
        <div
          role="listbox"
          aria-multiselectable="true"
          className="absolute left-0 top-[36px] z-[999] min-w-[220px] rounded-xl border-2 border-pink-200 bg-white p-2 shadow-2xl"
        >
          <div className="max-h-[260px] overflow-y-auto">
            {opcoes.map((opcao) => {
              const marcado = selecionados.includes(opcao.value);

              return (
                <label
                  key={opcao.value}
                  className={`flex cursor-pointer items-center gap-2 rounded-lg px-2 py-2 text-[11px] transition ${
                    marcado
                      ? "bg-pink-50 text-pink-800"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={marcado}
                    onChange={() => alternar(opcao.value)}
                    className="h-3.5 w-3.5 accent-pink-600"
                  />
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="shrink-0">{opcao.icon}</span>
                    <span className="truncate font-semibold">
                      {opcao.label}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          {selecionados.length > 0 && (
            <div className="mt-2 border-t border-slate-100 pt-2">
              <button
                type="button"
                onClick={() => onChange?.([])}
                className="w-full rounded-lg px-2 py-1.5 text-[10px] font-bold text-red-600 hover:bg-red-50"
              >
                Limpar seleção
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

export default function Leadsv3() {
  const { empresaAtiva } = useEmpresa();
  const [, startTransition] = useTransition();

  const tbodyRef = useRef(null);
  const tableScrollRef = useRef(null);
  const perfLiteRef = useRef({ t0: 0 });
  const nomeCacheRef = useRef(new WeakMap());
  const fetchLeadsRef = useRef(null);
  const scrollRafRef = useRef(0);
  const requestSeqRef = useRef(0);
  const ultimaConsultaDuplicidadeRef = useRef("");
  const contextoSorrisoTarefaRef = useRef(null);
  const idsTarefasAntesSorrisoRef = useRef(new Set());
  const contextoSorrisoInteracaoRef = useRef(null);
  const idsInteracoesAntesSorrisoRef = useRef(new Set());

  const usuarioLogado = useMemo(() => {
    if (typeof window === "undefined") return {};
    try {
      return JSON.parse(localStorage.getItem("usuarioLogado") || "{}");
    } catch {
      return {};
    }
  }, []);

  const empresaId = empresaAtiva?.id ?? usuarioLogado?.empresa_id ?? null;


  const podeGerenciarDataFechamento = useMemo(
    () => ["admin", "administrador", "supervisor"].includes(norm(usuarioLogado?.cargo)),
    [usuarioLogado?.cargo],
  );

  const [loadingLeads, setLoadingLeads] = useState(false);
  const [loadingBootstrap, setLoadingBootstrap] = useState(false);

  const [virtualScrollTop, setVirtualScrollTop] = useState(0);
  const [virtualViewportHeight, setVirtualViewportHeight] = useState(600);

  const [leads, setLeads] = useState([]);
  const [todasLeads, setTodasLeads] = useState([]);
  const [tarefas, setTarefas] = useState([]);
  const [interacoes, setInteracoes] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);
  const [recebLeads, setRecebLeads] = useState([]);
  const [
    solicitacoesTransferenciaPendentes,
    setSolicitacoesTransferenciaPendentes,
  ] = useState([]);

  const [procedimentos, setProcedimentos] = useState([]);
  const [regioes, setRegioes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [dentistas, setDentistas] = useState([]);
  const [fontes, setFontes] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [campanhas, setCampanhas] = useState([]);

  const [mostrarLegenda, setMostrarLegenda] = useState(false);
  const [modalRecebimentoAberto, setModalRecebimentoAberto] = useState(false);
  const [leadSelecionada, setLeadSelecionada] = useState(null);
  const [leadSelecionadaLogs, setLeadSelecionadaLogs] = useState(null);
  const [mostrarLogsModal, setMostrarLogsModal] = useState(false);
  const [interacoesModalAberto, setInteracoesModalAberto] = useState(false);
  const [leadSelecionadaParaInteracao, setLeadSelecionadaParaInteracao] =
    useState(null);
  const [interacoesDaLead, setInteracoesDaLead] = useState([]);
  const [modalAberto, setModalAberto] = useState(false);
  const [tarefasDaLead, setTarefasDaLead] = useState([]);
  const [modalAgendamentoAberto, setModalAgendamentoAberto] = useState(false);
  const [agendamentoSelecionado, setAgendamentoSelecionado] = useState(null);
  const [leadChatAberto, setLeadChatAberto] = useState(null);
  const [badgesWhatsapp, setBadgesWhatsapp] = useState({});
  const [modalTransferenciaAberto, setModalTransferenciaAberto] =
    useState(false);
  const [leadSelecionadaTransferencia, setLeadSelecionadaTransferencia] =
    useState(null);
  const [modalObservacaoAberto, setModalObservacaoAberto] = useState(false);
  const [leadObservacaoSelecionada, setLeadObservacaoSelecionada] =
    useState(null);
  const [textoObservacaoPaciente, setTextoObservacaoPaciente] = useState("");
  const [salvandoObservacaoPaciente, setSalvandoObservacaoPaciente] =
    useState(false);

  const [modalDataFechamentoAberto, setModalDataFechamentoAberto] =
    useState(false);
  const [leadDataFechamentoSelecionada, setLeadDataFechamentoSelecionada] =
    useState(null);
  const [dataFechamentoManual, setDataFechamentoManual] = useState("");
  const [fechadoPorManual, setFechadoPorManual] = useState("");
  const [motivoDataFechamento, setMotivoDataFechamento] = useState("");
  const [salvandoDataFechamento, setSalvandoDataFechamento] = useState(false);

  const [mostrarSorrisoPlanejado, setMostrarSorrisoPlanejado] = useState(false);
  const [carregandoSorrisoPlanejado, setCarregandoSorrisoPlanejado] = useState(false);

  const [mostrarInativas, setMostrarInativas] = useState(false);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [erros, setErros] = useState({});
  const [duplicidadeTelefoneAviso, setDuplicidadeTelefoneAviso] =
    useState(null);

  const [filtroPotencial, setFiltroPotencial] = useState("");
  const [filtroStatus, setFiltroStatus] = useState([]);
  const [filtroDentista, setFiltroDentista] = useState("");
  const [filtroFonte, setFiltroFonte] = useState("");
  const [filtroCaptador, setFiltroCaptador] = useState("");
  const [filtroRegiao, setFiltroRegiao] = useState("");
  const [filtroCampanha, setFiltroCampanha] = useState("");
  const [filtroStatusNegociacao, setFiltroStatusNegociacao] = useState([]);
  const [filtroProcedimento, setFiltroProcedimento] = useState("");
  const [filtroDataAgendamento, setFiltroDataAgendamento] = useState("");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [erroData, setErroData] = useState(false);
  const [tipoDataFiltro, setTipoDataFiltro] = useState("criado_em");
  const [nomeUI, setNomeUI] = useState("");
  const [telUI, setTelUI] = useState("");
  const [filtroLeadIdUrl, setFiltroLeadIdUrl] = useState("");
  const [filtroUrlAtivo, setFiltroUrlAtivo] = useState(false);
  const [filtroPendenciasHoje, setFiltroPendenciasHoje] = useState(false);
  const [filtroTarefasAtrasadas, setFiltroTarefasAtrasadas] = useState(false);
  const [filtroSemTarefa, setFiltroSemTarefa] = useState(false);
  const [filtroOrcamentoSemOportunidade, setFiltroOrcamentoSemOportunidade] =
    useState(false);
  const [soMeusLeads, setSoMeusLeads] = useState(false);
  const [leadsSelecionadasIds, setLeadsSelecionadasIds] = useState(
    () => new Set(),
  );

  const [centralDisparosAberta, setCentralDisparosAberta] = useState(false);

  const nomeFiltro = useDebouncedValue(nomeUI, 200);
  const telFiltro = useDebouncedValue(telUI, 200);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const params = new URLSearchParams(window.location.search);

    const leadId =
      params.get("lead_id") || params.get("leadId") || params.get("id") || "";

    const telefone =
      params.get("telefone") || params.get("tel") || params.get("phone") || "";

    const nome =
      params.get("nome") || params.get("nome_lead") || params.get("name") || "";

    if (!leadId && !telefone && !nome) return;

    setFiltroUrlAtivo(true);

    if (leadId) setFiltroLeadIdUrl(String(leadId).trim());
    if (telefone) setTelUI(String(telefone).replace(/\D/g, ""));
    if (nome) setNomeUI(nome);

    setDataInicio("");
    setDataFim("");
    setFiltroDataAgendamento("");
  }, []);

  const [form, setForm] = useState({
    data_lead: "",
    nome_lead: "",
    telefone1: "",
    total_orcamento: "",
    total_contratado: "",
    data_agendamento: "",
    id_procedimento: "",
    id_regiao: "",
    id_usuario: usuarioLogado?.id || "",
    usuario_criador_id: "",
    id_dentista: "",
    id_fonte_lead: "",
    empresa_id: usuarioLogado?.empresa_id || "",
    status_agendamento: "",
    potencial: "",
    status_negociacao: "",
    id_campanha: "",
  });

  useRenderCounter("Leadsv3.jsx");

  useEffect(() => {
    setLeadsSelecionadasIds(new Set());
  }, [empresaId]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    initLeadsTelemetry({
      apiBase: getApiBase(),
      empresaId: empresaAtiva?.id ?? usuarioLogado?.empresa_id ?? null,
      userId: usuarioLogado?.id ?? null,
      tableRootSelector: "#tabela-leads-v3",
      sampleRate: IS_TURBO ? 0.3 : 0.2,
      flushIntervalMs: 10000,
      clientVersion: `${window.CAPTA_MODE}-v3`,
    });
  }, [empresaAtiva?.id, usuarioLogado?.empresa_id, usuarioLogado?.id]);

  useEffect(() => {
    if (dataInicio && dataFim) {
      const inicio = new Date(dataInicio);
      const fim = new Date(dataFim);
      if (fim < inicio) {
        setErroData(true);
        toast.error("⚠️ A Data Fim não pode ser menor que a Data Início");
      } else {
        setErroData(false);
      }
    } else {
      setErroData(false);
    }
  }, [dataInicio, dataFim]);

  const getNomeById = useCallback((id, lista) => {
    if (!Array.isArray(lista)) return id ?? "";

    let map = nomeCacheRef.current.get(lista);
    if (!map) {
      map = new Map();
      for (const it of lista) {
        map.set(
          String(it.id),
          it?.nome ||
            it?.nome_fantasia ||
            it?.profissional ||
            it?.regiao ||
            it?.id ||
            "",
        );
      }
      nomeCacheRef.current.set(lista, map);
    }

    return map.get(String(id)) ?? id ?? "";
  }, []);

  const podeLancarRecebimento = useCallback((lead) => {
    if (!lead?.id) return false;

    return STATUS_NEGOCIACAO_RECEBIMENTO_NORM.has(
      norm(lead?.status_negociacao),
    );
  }, []);

  const abrirModalRecebimento = useCallback(
    (lead) => {
      if (!lead?.id) {
        toast.warning("Selecione uma lead válida para lançar recebimento.");
        return;
      }

      if (!podeLancarRecebimento(lead)) {
        toast.warning(
          "Não é permitido lançar recebimento. A negociação precisa estar como Fechado ou Fechado Parcial.",
        );
        return;
      }

      setLeadSelecionada(lead);
      setModalRecebimentoAberto(true);
    },
    [podeLancarRecebimento],
  );

  const fetchLeads = useCallback(async () => {
    const seq = ++requestSeqRef.current;

    const mark = appMarkStart("leadsv3:fetchLeads", {
      tipoDataFiltro,
      mostrarInativas,
      dataInicio,
      dataFim,
      filtroDataAgendamento: filtroDataAgendamento?.slice?.(0, 10) || null,
    });

    setLoadingLeads(true);
    perfLiteRef.current.t0 = performance.now();

    try {
      const empresaIdLocal = empresaAtiva?.id;
      if (!empresaIdLocal) {
        if (seq === requestSeqRef.current) setLeads([]);
        return;
      }

      const tipo = (tipoDataFiltro || "").toLowerCase().trim();
      let leadsArr = [];

      if (tipo === "data_agendamento") {
        const start =
          (filtroDataAgendamento && filtroDataAgendamento.slice(0, 10)) ||
          (dataInicio && dataInicio.slice(0, 10)) ||
          null;

        const end =
          (filtroDataAgendamento && filtroDataAgendamento.slice(0, 10)) ||
          (dataFim && dataFim.slice(0, 10)) ||
          null;

        if (start && end) {
          const endExclusivo = addDaysLocal(end, 1);
          const token = localStorage.getItem("token");
          const url = `https://api.captacrm.com.br/agendamentos/leads-filtrados/${empresaIdLocal}?start=${encodeURIComponent(
            start,
          )}&end=${encodeURIComponent(endExclusivo)}`;

          const resp = await fetch(url, {
            headers: {
              Authorization: token ? `Bearer ${token}` : "",
              "Content-Type": "application/json",
            },
          });

          if (!resp.ok) throw new Error("Erro ao buscar leads por agendamento");
          const lista = await resp.json();
          leadsArr = Array.isArray(lista) ? lista : [];
        } else {
          const urlAll = `/leads?empresa_id=${empresaIdLocal}&mostrarInativas=${
            mostrarInativas || filtroUrlAtivo ? "true" : "false"
          }`;
          const resAll = await api.get(urlAll);
          leadsArr = Array.isArray(resAll?.data)
            ? resAll.data
            : Array.isArray(resAll)
              ? resAll
              : resAll?.data?.leads || [];
        }
      } else {
        let url = `/leads?empresa_id=${empresaIdLocal}&mostrarInativas=${
          mostrarInativas || filtroUrlAtivo ? "true" : "false"
        }`;

        if (tipo === "criado_em") {
          if (dataInicio)
            url += `&criado_em_inicio=${encodeURIComponent(dataInicio)}`;
          if (dataFim) url += `&criado_em_fim=${encodeURIComponent(dataFim)}`;
        }

        const res = await api.get(url);
        leadsArr = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : res?.data?.leads || [];
      }

      if (seq === requestSeqRef.current) {
        setLeads(leadsArr);
      }
    } catch (error) {
      console.error("❌ Leadsv3 fetchLeads:", error);
      toast.error("Erro ao carregar leads na V3");
      if (seq === requestSeqRef.current) {
        setLeads([]);
      }
    } finally {
      if (seq === requestSeqRef.current) {
        setLoadingLeads(false);
      }
      if (mark) appMarkEnd(mark);
    }
  }, [
    empresaAtiva?.id,
    tipoDataFiltro,
    mostrarInativas,
    filtroUrlAtivo,
    dataInicio,
    dataFim,
    filtroDataAgendamento,
  ]);

  useEffect(() => {
    fetchLeadsRef.current = fetchLeads;
  }, [fetchLeads]);

  const refreshLeads = useCallback(async () => {
    if (fetchLeadsRef.current) {
      await fetchLeadsRef.current();
    }
  }, []);

  const fetchTodasLeads = useCallback(async () => {
    try {
      const empresaIdLocal = empresaAtiva?.id;
      if (!empresaIdLocal) {
        setTodasLeads([]);
        return;
      }

      const res = await api.get(
        `/leads?empresa_id=${empresaIdLocal}&mostrarInativas=true`,
      );
      const leadsArr = Array.isArray(res)
        ? res
        : Array.isArray(res?.data)
          ? res.data
          : res?.data?.leads || [];

      setTodasLeads(leadsArr);
    } catch (error) {
      console.error("❌ Leadsv3 fetchTodasLeads:", error);
      setTodasLeads([]);
    }
  }, [empresaAtiva?.id]);

  const carregarTarefas = useCallback(async () => {
    try {
      const empresaIdLocal = empresaAtiva?.id;
      const token = localStorage.getItem("token");

      if (!empresaIdLocal) {
        setTarefas([]);
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/todas-tarefas?empresa_id=${empresaIdLocal}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        },
      );

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setTarefas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Leadsv3 carregarTarefas:", error);
      setTarefas([]);
    }
  }, [empresaAtiva?.id]);

  const carregarInteracoes = useCallback(async () => {
    try {
      const empresaIdLocal = empresaAtiva?.id;
      const token = localStorage.getItem("token");

      if (!empresaIdLocal) {
        setInteracoes([]);
        return;
      }

      const resp = await fetch(
        `${import.meta.env.VITE_API_BASE_URL}/todas-interacoes?empresa_id=${empresaIdLocal}`,
        {
          headers: {
            Authorization: token ? `Bearer ${token}` : undefined,
            "Content-Type": "application/json",
          },
        },
      );

      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setInteracoes(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Leadsv3 carregarInteracoes:", error);
      setInteracoes([]);
    }
  }, [empresaAtiva?.id]);

  const finalizarMarcacaoTarefaSorriso = useCallback(async () => {
    const contexto = contextoSorrisoTarefaRef.current;
    if (!contexto) return;

    const empresa_id = empresaAtiva?.id ?? usuarioLogado?.empresa_id;
    const lead_id = Number(contexto?.lead_id || 0);

    try {
      if (!empresa_id || !lead_id) return;

      const res = await api.get("/tarefas", {
        params: { lead_id, empresa_id },
      });
      const lista = Array.isArray(res?.data) ? res.data : [];
      const anteriores = idsTarefasAntesSorrisoRef.current || new Set();
      const novosIds = lista
        .map((item) => Number(item?.id || 0))
        .filter((id) => id > 0 && !anteriores.has(String(id)));

      if (novosIds.length > 0) {
        await api.post("/sorriso-planejado/cobrancas/marcar-tarefas", {
          empresa_id,
          lead_id,
          tarefa_ids: novosIds,
          referencia_tipo: contexto.referencia_tipo,
          referencia_id: contexto.referencia_id,
        });
      }
    } catch (error) {
      console.error("❌ Erro ao identificar tarefa de cobrança do Sorriso Planejado:", error);
      toast.error(
        error?.response?.data?.erro ||
          "A tarefa foi salva, mas não foi possível vinculá-la à cobrança do Sorriso Planejado.",
      );
    } finally {
      contextoSorrisoTarefaRef.current = null;
      idsTarefasAntesSorrisoRef.current = new Set();
    }
  }, [empresaAtiva?.id, usuarioLogado?.empresa_id]);

  const finalizarMarcacaoInteracaoSorriso = useCallback(async () => {
    const contexto = contextoSorrisoInteracaoRef.current;
    if (!contexto) return;

    const empresa_id = empresaAtiva?.id ?? usuarioLogado?.empresa_id;
    const lead_id = Number(contexto?.lead_id || 0);

    try {
      if (!empresa_id || !lead_id) return;

      const res = await api.get("/interacoes", {
        params: { id_lead: lead_id, empresa_id },
      });
      const lista = Array.isArray(res?.data) ? res.data : [];
      const anteriores = idsInteracoesAntesSorrisoRef.current || new Set();
      const novosIds = lista
        .map((item) => Number(item?.id || 0))
        .filter((id) => id > 0 && !anteriores.has(String(id)));

      if (novosIds.length > 0) {
        await api.post("/sorriso-planejado/cobrancas/marcar-interacoes", {
          empresa_id,
          lead_id,
          interacao_ids: novosIds,
          referencia_tipo: contexto.referencia_tipo,
          referencia_id: contexto.referencia_id,
        });
      }
    } catch (error) {
      console.error("❌ Erro ao identificar interação de cobrança do Sorriso Planejado:", error);
      toast.error(
        error?.response?.data?.erro ||
          "A interação foi salva, mas não foi possível vinculá-la à cobrança do Sorriso Planejado.",
      );
    } finally {
      contextoSorrisoInteracaoRef.current = null;
      idsInteracoesAntesSorrisoRef.current = new Set();
    }
  }, [empresaAtiva?.id, usuarioLogado?.empresa_id]);

  const fetchAgendamentos = useCallback(async () => {
    try {
      const empresaIdLocal = empresaAtiva?.id;

      if (!empresaIdLocal) {
        setAgendamentos([]);
        return;
      }

      const res = await api.get(`/agendamentos/empresa/${empresaIdLocal}`);
      setAgendamentos(
        Array.isArray(res?.data) ? res.data : Array.isArray(res) ? res : [],
      );
    } catch (err) {
      console.error("❌ Leadsv3 fetchAgendamentos:", err);
      setAgendamentos([]);
    }
  }, [empresaAtiva?.id]);

  const fetchRecebimentos = useCallback(async () => {
    try {
      if (!empresaAtiva?.id) {
        setRecebLeads([]);
        return;
      }

      const token = localStorage.getItem("token");
      const res = await fetch(
        `https://api.captacrm.com.br/receb_leads/financeiro/${empresaAtiva.id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (!res.ok) throw new Error("Erro ao buscar recebimentos");
      const data = await res.json();
      setRecebLeads(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("❌ Leadsv3 fetchRecebimentos:", e);
      setRecebLeads([]);
    }
  }, [empresaAtiva?.id]);

  const fetchSolicitacoesTransferenciaPendentes = useCallback(async () => {
    try {
      const empresaIdLocal = empresaAtiva?.id;
      if (!empresaIdLocal) {
        setSolicitacoesTransferenciaPendentes([]);
        return;
      }

      const res = await api.get("/leads/transferencias/solicitacoes", {
        params: { empresa_id: empresaIdLocal, status: "pendente" },
      });

      const lista = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];

      console.log("📌 Pendências de transferência carregadas:", lista);
      setSolicitacoesTransferenciaPendentes(lista);
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data;

      console.error(
        "❌ Leadsv3 fetchSolicitacoesTransferenciaPendentes:",
        status,
        data || error,
      );

      toast.error(
        data?.erro ||
          data?.error ||
          `Erro ao carregar pendências de transferência (${status || "sem status"}).`,
      );

      setSolicitacoesTransferenciaPendentes([]);
    }
  }, [empresaAtiva?.id]);

  const fetchFontesEProcedimentos = useCallback(async () => {
    try {
      const [fontesRes, procedimentosRes] = await Promise.all([
        api.get("/fonteslead"),
        api.get("/procedimentos"),
      ]);

      const fontesArr = Array.isArray(fontesRes)
        ? fontesRes
        : Array.isArray(fontesRes?.data)
          ? fontesRes.data
          : [];

      const procedimentosArr = Array.isArray(procedimentosRes)
        ? procedimentosRes
        : Array.isArray(procedimentosRes?.data)
          ? procedimentosRes.data
          : [];

      setFontes(fontesArr);
      setProcedimentos(procedimentosArr);
    } catch (error) {
      console.error("❌ Leadsv3 fetchFontesEProcedimentos:", error);
      setFontes([]);
      setProcedimentos([]);
    }
  }, []);

  const fetchRegioes = useCallback(async () => {
    try {
      const res = await api.get("/regioes");
      const arr = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res)
          ? res
          : [];
      setRegioes(arr);
    } catch (error) {
      console.error("❌ Leadsv3 fetchRegioes:", error);
      setRegioes([]);
    }
  }, []);

  const fetchUsuarios = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const empresaIdLocal = empresaAtiva?.id;

      if (!empresaIdLocal) {
        setUsuarios([]);
        return;
      }

      const response = await fetch(
        `https://api.captacrm.com.br/usuarios?empresa_id=${empresaIdLocal}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );

      const data = await response.json();
      setUsuarios(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Leadsv3 fetchUsuarios:", error);
      setUsuarios([]);
    }
  }, [empresaAtiva?.id]);

  const fetchDentistas = useCallback(async () => {
    try {
      const id = empresaAtiva?.id;
      if (!id) {
        setDentistas([]);
        return;
      }

      const token = localStorage.getItem("token");
      const r = await fetch(`https://api.captacrm.com.br/dentistas/${id}`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setDentistas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Leadsv3 fetchDentistas:", error);
      setDentistas([]);
    }
  }, [empresaAtiva?.id]);

  const fetchEmpresas = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch("https://api.captacrm.com.br/empresas", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!res.ok) throw new Error(`Erro ${res.status}`);
      const data = await res.json();
      setEmpresas(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("❌ Leadsv3 fetchEmpresas:", error);
      setEmpresas([]);
    }
  }, []);

  const fetchCampanhas = useCallback(async () => {
    try {
      const empresaIdLocal = empresaAtiva?.id ?? usuarioLogado?.empresa_id;

      if (!empresaIdLocal) {
        setCampanhas([]);
        return;
      }

      const API = (
        import.meta?.env?.VITE_API_BASE_URL || "https://api.captacrm.com.br"
      ).replace(/\/$/, "");
      const raw =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("jwt") ||
        "";
      const token = raw
        ? raw.startsWith("Bearer ")
          ? raw
          : `Bearer ${raw}`
        : "";

      const res = await fetch(`${API}/campanhas?empresa_id=${empresaIdLocal}`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: token } : {}),
        },
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data?.erro || data?.error || res.statusText);

      const lista = Array.isArray(data)
        ? data
        : Array.isArray(data?.campanhas)
          ? data.campanhas
          : [];
      setCampanhas(lista);
    } catch (error) {
      console.error("❌ Leadsv3 fetchCampanhas:", error);
      setCampanhas([]);
    }
  }, [empresaAtiva?.id, usuarioLogado?.empresa_id]);

  useEffect(() => {
    if (!empresaAtiva?.id) return;

    let ativo = true;

    async function bootstrap() {
      setLoadingBootstrap(true);
      const m = appMarkStart("leadsv3:bootstrap", {
        empresaId: empresaAtiva?.id,
      });

      try {
        await Promise.all([
          carregarTarefas(),
          carregarInteracoes(),
          fetchAgendamentos(),
          fetchFontesEProcedimentos(),
          fetchRegioes(),
          fetchUsuarios(),
          fetchDentistas(),
          fetchCampanhas(),
          fetchRecebimentos(),
          fetchSolicitacoesTransferenciaPendentes(),
        ]);
      } catch (err) {
        console.error("❌ Erro no bootstrap da Leadsv3:", err);
        if (ativo) toast.error("Erro ao carregar dados iniciais da LeadsV3");
      } finally {
        if (m) appMarkEnd(m);
        if (ativo) setLoadingBootstrap(false);
      }
    }

    bootstrap();
    return () => {
      ativo = false;
    };
  }, [
    empresaAtiva?.id,
    carregarTarefas,
    carregarInteracoes,
    fetchAgendamentos,
    fetchFontesEProcedimentos,
    fetchRegioes,
    fetchUsuarios,
    fetchDentistas,
    fetchCampanhas,
    fetchRecebimentos,
    fetchSolicitacoesTransferenciaPendentes,
  ]);

  useEffect(() => {
    if (!mostrarFormulario || !empresaAtiva?.id || todasLeads.length > 0)
      return;
    fetchTodasLeads();
  }, [mostrarFormulario, empresaAtiva?.id, todasLeads.length, fetchTodasLeads]);

  useEffect(() => {
    if (!mostrarFormulario || empresas.length > 0) return;
    fetchEmpresas();
  }, [mostrarFormulario, empresas.length, fetchEmpresas]);

  useEffect(() => {
    if (norm(tipoDataFiltro) !== "data_recebimento" || recebLeads.length > 0) {
      return;
    }
    fetchRecebimentos();
  }, [tipoDataFiltro, recebLeads.length, fetchRecebimentos]);

  useEffect(() => {
    if (!empresaAtiva?.id || erroData) return;
    fetchLeads();
  }, [
    empresaAtiva?.id,
    erroData,
    dataInicio,
    dataFim,
    mostrarInativas,
    filtroDataAgendamento,
    tipoDataFiltro,
    fetchLeads,
  ]);

  useEffect(() => {
    if (!empresaAtiva?.id) return;
    fetchSolicitacoesTransferenciaPendentes();
  }, [empresaAtiva?.id, fetchSolicitacoesTransferenciaPendentes, leads.length]);

  useEffect(() => {
    if (loadingLeads) return;
    const t0 = perfLiteRef.current.t0;
    if (!t0) return;

    const ms = Math.round(performance.now() - t0);
    perfLiteRef.current.t0 = 0;

    postPerfLite({
      empresaId,
      userId: usuarioLogado?.id ?? null,
      app_ms: ms,
    });
  }, [loadingLeads, empresaId, usuarioLogado?.id]);

  const recebPorLead = useMemo(() => {
    const map = new Map();
    for (const r of recebLeads || []) {
      const k = String(r.lead_id);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(r);
    }
    return map;
  }, [recebLeads]);

  const tarefasPorLead = useMemo(() => {
    const map = new Map();
    for (const t of tarefas || []) {
      const k = String(t.lead_id);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(t);
    }
    return map;
  }, [tarefas]);

  const interacoesPorLead = useMemo(() => {
    const map = new Map();
    for (const i of interacoes || []) {
      const k = String(i.id_lead ?? i.lead_id);
      if (!map.has(k)) map.set(k, []);
      map.get(k).push(i);
    }
    return map;
  }, [interacoes]);

  const ultimoAgendamentoPorLead = useMemo(() => {
    const map = new Map();
    for (const a of agendamentos || []) {
      const leadId = String(a?.lead_id ?? a?.id_lead ?? "").trim();
      if (!leadId) continue;

      const atual = map.get(leadId);
      if (!atual || Number(a.id) > Number(atual.id)) {
        map.set(leadId, a);
      }
    }
    return map;
  }, [agendamentos]);

  const agendamentosPorLead = useMemo(() => {
    const map = new Map();

    for (const agendamento of agendamentos || []) {
      const leadId = String(
        agendamento?.lead_id ?? agendamento?.id_lead ?? "",
      ).trim();
      if (!leadId) continue;

      if (!map.has(leadId)) map.set(leadId, []);
      map.get(leadId).push(agendamento);
    }

    return map;
  }, [agendamentos]);

  const solicitacaoPendentePorLead = useMemo(() => {
    const map = new Map();

    for (const s of solicitacoesTransferenciaPendentes || []) {
      const leadId = String(s?.lead_id || "").trim();
      const status = String(s?.status || "")
        .trim()
        .toLowerCase();

      if (!leadId) continue;
      if (status && status !== "pendente") continue;

      if (!map.has(leadId)) {
        map.set(leadId, s);
      }
    }

    return map;
  }, [solicitacoesTransferenciaPendentes]);

  const temTransferenciaPendente = useCallback(
    (leadId) => solicitacaoPendentePorLead.has(String(leadId || "").trim()),
    [solicitacaoPendentePorLead],
  );

  const leadsIndexadas = useMemo(() => {
    const seen = new Set();
    const dedup = [];

    for (const l of leads || []) {
      if (!seen.has(l.id)) {
        seen.add(l.id);
        dedup.push({
          ...l,
          _nome_norm: norm(l.nome_lead || ""),
          _tel_digits: (l.telefone1 || "").replace(/\D/g, ""),
          _status_neg_norm: norm(l.status_negociacao),
          _data_lead_num: soData(l.data_lead),
          _criado_em_num: soData(l.criado_em),
          _ultimo_agendamento: ultimoAgendamentoPorLead.get(String(l.id)) || null,
          _solicitacao_transferencia_pendente:
            solicitacaoPendentePorLead.get(String(l.id)) ||
            l._solicitacao_transferencia_pendente ||
            null,
        });
      }
    }

    return dedup;
  }, [leads, ultimoAgendamentoPorLead, solicitacaoPendentePorLead]);

  const leadsFiltradas = useMemo(() => {
    const M2 = appMarkStart("leadsv3:filter", {
      tipo: tipoDataFiltro || null,
      nome: !!nomeFiltro,
      tel: !!telFiltro,
    });

    if (mostrarFormulario) {
      if (M2) appMarkEnd(M2);
      return leadsIndexadas;
    }

    const hojeStr = new Date().toISOString().slice(0, 10);
    const ini = dataInicio ? soData(dataInicio) : null;
    const fim = dataFim ? soData(dataFim) : null;
    const filtroStatusSelecionados = Array.isArray(filtroStatus)
      ? filtroStatus
      : filtroStatus
        ? [filtroStatus]
        : [];
    const hojeNum = soData(new Date());
    const agora = new Date();
    const inicioMesAtual = new Date(agora.getFullYear(), agora.getMonth(), 1);
    const inicioMesAtualNum = soData(inicioMesAtual);
    const filtroNomeNorm = nomeFiltro ? norm(nomeFiltro) : "";
    const telFiltroDigits = telFiltro ? telFiltro.replace(/\D/g, "") : "";
    const filtroLeadIdUrlStr = filtroLeadIdUrl
      ? String(filtroLeadIdUrl).trim()
      : "";
    const tipoNorm = norm(tipoDataFiltro);

    const result = leadsIndexadas.filter((lead) => {
      const leadIdStr = String(lead.id);

      if (filtroLeadIdUrlStr && leadIdStr !== filtroLeadIdUrlStr) {
        return false;
      }

      const agendamentoRef = lead._ultimo_agendamento;

      if (filtroStatusSelecionados.length > 0) {
        const statusUltimo = norm(agendamentoRef?.status_agendamento);
        const dataUltimo = soData(agendamentoRef?.data_agendamento);
        const agendamentosDaLead = agendamentosPorLead.get(leadIdStr) || [];

        const correspondeAoStatus = filtroStatusSelecionados.some((valor) => {
          if (valor === "__sem_agendamento__") {
            // "Sem Agendamento" representa paciente sem agenda válida para o mês atual/futuro.
            // Entra quando:
            // - nunca teve agendamento; OU
            // - o último status é Faltou ou Cancelado; OU
            // - o último agendamento é de qualquer mês anterior ao mês atual,
            //   inclusive quando o histórico antigo está como Compareceu.
            // Sai do filtro se existir Agendado/Reagendado/Confirmado para hoje ou futuro.
            const temAgendamentoAtivoHojeOuFuturo = agendamentosDaLead.some(
              (agendamento) => {
                const status = norm(agendamento?.status_agendamento);
                const data = soData(agendamento?.data_agendamento);

                return (
                  data !== null &&
                  hojeNum !== null &&
                  data >= hojeNum &&
                  STATUS_AGENDAMENTO_ATIVO_NORM.has(status)
                );
              },
            );

            if (temAgendamentoAtivoHojeOuFuturo) return false;
            if (!agendamentoRef) return true;

            const ultimoFaltou = statusUltimo === "faltou";
            const ultimoCancelado = statusUltimo.startsWith("cancel");
            const ultimoDeMesAnteriorOuMaisAntigo =
              dataUltimo !== null &&
              inicioMesAtualNum !== null &&
              dataUltimo < inicioMesAtualNum;

            return (
              ultimoFaltou ||
              ultimoCancelado ||
              ultimoDeMesAnteriorOuMaisAntigo
            );
          }

          if (!agendamentoRef) return false;

          const statusDesejado = norm(valor);
          if (statusDesejado === "cancelado") {
            return statusUltimo.startsWith("cancel");
          }

          return statusUltimo === statusDesejado;
        });

        if (!correspondeAoStatus) return false;
      }

      if ((dataInicio || dataFim) && tipoNorm === "data_recebimento") {
        const recs = recebPorLead.get(leadIdStr) || [];
        if (recs.length === 0) return false;

        const ok = recs.some((r) => {
          const nr = soData(r.data_recebimento);
          if (!nr) return false;
          if (ini !== null && nr < ini) return false;
          if (fim !== null && nr > fim) return false;
          return true;
        });

        if (!ok) return false;
      }

      if ((dataInicio || dataFim) && tipoNorm === "data_lead") {
        if (ini !== null && (!lead._data_lead_num || lead._data_lead_num < ini))
          return false;
        if (fim !== null && (!lead._data_lead_num || lead._data_lead_num > fim))
          return false;
      }

      if ((dataInicio || dataFim) && tipoNorm === "criado_em") {
        if (ini !== null && (!lead._criado_em_num || lead._criado_em_num < ini))
          return false;
        if (fim !== null && (!lead._criado_em_num || lead._criado_em_num > fim))
          return false;
      }

      if (filtroPotencial && lead.potencial !== filtroPotencial) return false;
      if (
        filtroStatusNegociacao.length > 0 &&
        !filtroStatusNegociacao.includes(lead.status_negociacao)
      )
        return false;
      if (filtroDentista && String(lead.id_dentista) !== String(filtroDentista))
        return false;
      if (filtroFonte && String(lead.id_fonte_lead) !== String(filtroFonte))
        return false;
      if (
        filtroCaptador &&
        String(lead.usuario_criador_id) !== String(filtroCaptador)
      )
        return false;
      if (filtroRegiao && String(lead.id_regiao) !== String(filtroRegiao))
        return false;
      if (filtroCampanha && String(lead.id_campanha) !== String(filtroCampanha))
        return false;
      if (
        filtroProcedimento &&
        String(lead.id_procedimento) !== String(filtroProcedimento)
      )
        return false;
      if (filtroNomeNorm && !lead._nome_norm.includes(filtroNomeNorm))
        return false;
      if (telFiltroDigits && !lead._tel_digits.includes(telFiltroDigits))
        return false;

      if (!filtroUrlAtivo && typeof mostrarInativas !== "undefined") {
        if (mostrarInativas && !STATUS_INATIVAS_NORM.has(lead._status_neg_norm))
          return false;
        if (!mostrarInativas && STATUS_INATIVAS_NORM.has(lead._status_neg_norm))
          return false;
      }

      const ts = tarefasPorLead.get(leadIdStr) || [];

      if (filtroPendenciasHoje) {
        const ok = ts.some(
          (t) =>
            t.data_vencimento?.slice(0, 10) === hojeStr &&
            norm(t.status) !== "concluido",
        );
        if (!ok) return false;
      }

      if (filtroTarefasAtrasadas) {
        const tarefasAtrasadas = ts.filter((t) => {
          const statusTarefa = norm(t?.status || "");

          return (
            t?.data_vencimento?.slice(0, 10) < hojeStr &&
            !STATUS_TAREFA_CONCLUIDA_NORM.has(statusTarefa)
          );
        });

        if (!tarefasAtrasadas.length) return false;

        const temTarefaAtrasadaPosVenda = tarefasAtrasadas.some((t) =>
          STATUS_TAREFA_POS_VENDA_NORM.has(norm(t?.status || "")),
        );

        // Regra operacional:
        // Lead Fechada continua fora do filtro Atraso, exceto quando existir
        // uma tarefa atrasada com status estruturado "Pós Venda".
        // Não usamos título/descrição para classificar pós-venda.
        if (lead._status_neg_norm === "fechado" && !temTarefaAtrasadaPosVenda)
          return false;
      }

      if (filtroSemTarefa) {
        if (
          STATUS_INATIVAS_NORM.has(lead._status_neg_norm) ||
          lead._status_neg_norm === "fechado"
        )
          return false;

        const temPendente = ts.some((t) => {
          const s = norm(t?.status || "");
          return s !== "" && !STATUS_TAREFA_CONCLUIDA_NORM.has(s);
        });

        if (temPendente) return false;
      }

      if (filtroOrcamentoSemOportunidade) {
        if (!isLeadOrcamentoSemOportunidadeOuOportunidadeMenorRecebido(lead)) {
          return false;
        }
      }

      return true;
    });

    if (M2) appMarkEnd(M2);
    return result;
  }, [
    leadsIndexadas,
    tipoDataFiltro,
    nomeFiltro,
    telFiltro,
    filtroLeadIdUrl,
    filtroUrlAtivo,
    mostrarFormulario,
    filtroStatus,
    dataInicio,
    dataFim,
    recebPorLead,
    filtroPotencial,
    filtroStatusNegociacao,
    filtroDentista,
    filtroFonte,
    filtroCaptador,
    filtroRegiao,
    filtroCampanha,
    filtroProcedimento,
    mostrarInativas,
    tarefasPorLead,
    filtroPendenciasHoje,
    filtroTarefasAtrasadas,
    filtroSemTarefa,
    agendamentosPorLead,
    filtroOrcamentoSemOportunidade,
  ]);

  const resumoFinanceiro = useMemo(() => {
    let total = 0;
    let fechado = 0;

    for (const lead of leadsFiltradas) {
      total += toMoneyNum(lead.total_orcamento);
      const recebimentosDaLead = recebPorLead.get(String(lead.id)) || [];
      fechado += somarRecebimentosLead(recebimentosDaLead);
    }

    return { total, fechado };
  }, [leadsFiltradas, recebPorLead]);

  const leadsOrdenadas = useMemo(() => {
    const M3 = appMarkStart("leadsv3:sort", { n: leadsFiltradas.length });

    const prioridadeCor = { vermelho: 1, azul: 2, amarelo: 3 };
    const prioridadeStatus = STATUS_NEGOCIACAO_PRIORIDADE;

    const arr = [...leadsFiltradas].sort((a, b) => {
      const aBadge = badgesWhatsapp[a.id] || {};
      const bBadge = badgesWhatsapp[b.id] || {};
      const pa = prioridadeCor[aBadge.status] || 999;
      const pb = prioridadeCor[bBadge.status] || 999;
      if (pa !== pb) return pa - pb;

      const ca = aBadge.count || 0;
      const cb = bBadge.count || 0;
      if (ca !== cb) return cb - ca;

      const iaRaw = prioridadeStatus.indexOf(
        (a.status_negociacao || "").trim(),
      );
      const ibRaw = prioridadeStatus.indexOf(
        (b.status_negociacao || "").trim(),
      );
      const ia = iaRaw === -1 ? 999 : iaRaw;
      const ib = ibRaw === -1 ? 999 : ibRaw;
      return ia - ib;
    });

    if (M3) appMarkEnd(M3);
    return arr;
  }, [leadsFiltradas, badgesWhatsapp]);

  const idsLeadsFiltradas = useMemo(
    () =>
      leadsOrdenadas
        .map((lead) => String(lead?.id ?? "").trim())
        .filter(Boolean),
    [leadsOrdenadas],
  );

  const totalLeadsSelecionadasNoFiltro = useMemo(
    () =>
      idsLeadsFiltradas.reduce(
        (total, leadId) =>
          total + (leadsSelecionadasIds.has(leadId) ? 1 : 0),
        0,
      ),
    [idsLeadsFiltradas, leadsSelecionadasIds],
  );

  const todasLeadsFiltradasSelecionadas =
    idsLeadsFiltradas.length > 0 &&
    totalLeadsSelecionadasNoFiltro === idsLeadsFiltradas.length;

  const alternarSelecaoLead = useCallback((leadId) => {
    const id = String(leadId ?? "").trim();
    if (!id) return;

    setLeadsSelecionadasIds((anteriores) => {
      const proximas = new Set(anteriores);

      if (proximas.has(id)) proximas.delete(id);
      else proximas.add(id);

      return proximas;
    });
  }, []);

  const alternarSelecaoTodasLeadsFiltradas = useCallback(() => {
    setLeadsSelecionadasIds((anteriores) => {
      const proximas = new Set(anteriores);

      if (todasLeadsFiltradasSelecionadas) {
        idsLeadsFiltradas.forEach((leadId) => proximas.delete(leadId));
      } else {
        idsLeadsFiltradas.forEach((leadId) => proximas.add(leadId));
      }

      return proximas;
    });
  }, [idsLeadsFiltradas, todasLeadsFiltradasSelecionadas]);

  const isLeadManutencaoAparelho = useCallback(
    (lead) => {
      const idProcedimento = String(lead?.id_procedimento ?? "").trim();
      const nomeProcedimento = norm(
        getNomeById(lead?.id_procedimento, procedimentos) ||
          lead?.procedimento ||
          lead?.nome_procedimento ||
          "",
      );
      const observacao = norm(lead?.observacao_paciente || "");

      return (
        idProcedimento === "9" ||
        nomeProcedimento.includes("manutencao") ||
        observacao.includes("recorrencia ortodontia") ||
        observacao.includes("carteira: recorrencia ortodontia") ||
        observacao.includes("carteira: recorrencia")
      );
    },
    [getNomeById, procedimentos],
  );

  const abrirModalDataFechamento = useCallback(
    (lead) => {
      if (!lead?.id) return;

      if (!podeGerenciarDataFechamento) {
        toast.warning(
          "Somente Administrador ou Supervisor pode informar/corrigir a data de fechamento.",
        );
        return;
      }

      const status = norm(lead?.status_negociacao);
      if (!['fechado', 'fechado parcial'].includes(status)) {
        toast.warning(
          "A data de fechamento só se aplica a Fechado ou Fechado Parcial.",
        );
        return;
      }

      const dataAtual = lead?.data_fechamento
        ? String(lead.data_fechamento).slice(0, 10)
        : "";

      setLeadDataFechamentoSelecionada(lead);
      setDataFechamentoManual(dataAtual);
      setFechadoPorManual(lead?.fechado_por ? String(lead.fechado_por) : "");
      setMotivoDataFechamento("");
      setModalDataFechamentoAberto(true);
    },
    [podeGerenciarDataFechamento],
  );

  const fecharModalDataFechamento = useCallback(() => {
    if (salvandoDataFechamento) return;
    setModalDataFechamentoAberto(false);
    setLeadDataFechamentoSelecionada(null);
    setDataFechamentoManual("");
    setFechadoPorManual("");
    setMotivoDataFechamento("");
  }, [salvandoDataFechamento]);

  const atualizarDataFechamentoLocal = useCallback((leadId, dados) => {
    const aplicar = (lista) =>
      (lista || []).map((lead) =>
        String(lead.id) === String(leadId)
          ? {
              ...lead,
              data_fechamento: dados?.data_fechamento || lead.data_fechamento,
              fechado_por:
                dados?.fechado_por === null || dados?.fechado_por === undefined
                  ? lead.fechado_por
                  : dados.fechado_por,
              data_fechamento_origem:
                dados?.data_fechamento_origem || lead.data_fechamento_origem,
              data_fechamento_registrada_por:
                dados?.data_fechamento_registrada_por ||
                lead.data_fechamento_registrada_por,
            }
          : lead,
      );

    setLeads((prev) => aplicar(prev));
    setTodasLeads((prev) => aplicar(prev));
  }, []);

  const salvarDataFechamento = useCallback(async () => {
    const lead = leadDataFechamentoSelecionada;
    if (!lead?.id) return;

    const data = String(dataFechamentoManual || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(data)) {
      toast.warning("Informe a data do fechamento.");
      return;
    }

    const hoje = new Date();
    const hojeYmd = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}-${String(hoje.getDate()).padStart(2, "0")}`;
    if (data > hojeYmd) {
      toast.warning("A data do fechamento não pode ser futura.");
      return;
    }

    const dataAnterior = lead?.data_fechamento
      ? String(lead.data_fechamento).slice(0, 10)
      : "";
    const corrigindo = Boolean(dataAnterior && dataAnterior !== data);

    if (corrigindo && String(motivoDataFechamento || "").trim().length < 10) {
      toast.warning("Informe o motivo da correção com pelo menos 10 caracteres.");
      return;
    }

    try {
      setSalvandoDataFechamento(true);
      const response = await api.post(`/leads/${lead.id}/data-fechamento`, {
        data_fechamento: data,
        fechado_por: fechadoPorManual ? Number(fechadoPorManual) : null,
        motivo: String(motivoDataFechamento || "").trim() || null,
      });

      const dados = response?.data ?? response;
      atualizarDataFechamentoLocal(lead.id, dados);
      toast.success(dados?.mensagem || "Data de fechamento salva com sucesso!");
      setModalDataFechamentoAberto(false);
      setLeadDataFechamentoSelecionada(null);
      setDataFechamentoManual("");
      setFechadoPorManual("");
      setMotivoDataFechamento("");
      await refreshLeads();
    } catch (err) {
      console.error("❌ Erro ao salvar data de fechamento:", err);
      toast.error(
        err?.response?.data?.erro ||
          err?.response?.data?.error ||
          err?.response?.data?.detalhe ||
          err?.message ||
          "Erro ao salvar data de fechamento.",
      );
    } finally {
      setSalvandoDataFechamento(false);
    }
  }, [
    leadDataFechamentoSelecionada,
    dataFechamentoManual,
    fechadoPorManual,
    motivoDataFechamento,
    atualizarDataFechamentoLocal,
    refreshLeads,
  ]);

  const abrirModalObservacaoPaciente = useCallback((lead) => {
    if (!lead?.id) return;

    setLeadObservacaoSelecionada(lead);
    setTextoObservacaoPaciente(lead?.observacao_paciente || "");
    setModalObservacaoAberto(true);
  }, []);

  const fecharModalObservacaoPaciente = useCallback(() => {
    if (salvandoObservacaoPaciente) return;

    setModalObservacaoAberto(false);
    setLeadObservacaoSelecionada(null);
    setTextoObservacaoPaciente("");
  }, [salvandoObservacaoPaciente]);

  const atualizarObservacaoPacienteLocal = useCallback((leadId, observacao) => {
    const aplicar = (lista) =>
      (lista || []).map((lead) =>
        String(lead.id) === String(leadId)
          ? { ...lead, observacao_paciente: observacao }
          : lead,
      );

    setLeads((prev) => aplicar(prev));
    setTodasLeads((prev) => aplicar(prev));
  }, []);

  const salvarObservacaoPaciente = useCallback(async () => {
    const leadId = leadObservacaoSelecionada?.id;
    if (!leadId) return;

    const observacaoNormalizada = String(textoObservacaoPaciente || "").trim();
    const observacaoFinal = observacaoNormalizada || null;

    try {
      setSalvandoObservacaoPaciente(true);

      await api.put(`/leads/${leadId}`, {
        observacao_paciente: observacaoFinal,
      });

      atualizarObservacaoPacienteLocal(leadId, observacaoFinal);
      toast.success("Observação do paciente salva com sucesso!");
      setModalObservacaoAberto(false);
      setLeadObservacaoSelecionada(null);
      setTextoObservacaoPaciente("");
    } catch (err) {
      console.error("❌ Erro ao salvar observação do paciente:", err);
      toast.error(
        err?.response?.data?.error ||
          err?.response?.data?.erro ||
          err?.message ||
          "Erro ao salvar observação do paciente.",
      );
    } finally {
      setSalvandoObservacaoPaciente(false);
    }
  }, [
    leadObservacaoSelecionada?.id,
    textoObservacaoPaciente,
    atualizarObservacaoPacienteLocal,
  ]);

  const atualizarValorMinimoLocal = useCallback((leadId, valorMinimo) => {
    const aplicar = (lista) =>
      (lista || []).map((lead) =>
        String(lead.id) === String(leadId)
          ? { ...lead, valor_minimo: valorMinimo }
          : lead,
      );

    setLeads((prev) => aplicar(prev));
    setTodasLeads((prev) => aplicar(prev));
  }, []);

  const salvarValorMinimoDaTabela = useCallback(
    async (lead, valorMinimo) => {
      const leadId = lead?.id;
      if (!leadId) {
        toast.error(
          "Não foi possível identificar a lead para salvar o valor mínimo.",
        );
        return false;
      }

      const totalOrcamento = toMoneyNum(lead?.total_orcamento);

      if (
        valorMinimo !== null &&
        totalOrcamento > 0 &&
        valorMinimo > totalOrcamento
      ) {
        toast.warning(
          "O valor mínimo não pode ser maior que o valor total da oportunidade.",
        );
        return false;
      }

      try {
        await api.put(`/leads/${leadId}`, {
          valor_minimo: valorMinimo,
        });

        atualizarValorMinimoLocal(leadId, valorMinimo);
        toast.success(
          valorMinimo === null
            ? "Valor mínimo removido com sucesso!"
            : "Valor mínimo salvo com sucesso!",
        );
        return true;
      } catch (err) {
        console.error("❌ Erro ao salvar valor mínimo:", err);
        toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.erro ||
            err?.message ||
            "Erro ao salvar o valor mínimo.",
        );
        return false;
      }
    },
    [atualizarValorMinimoLocal],
  );

  const columns = useMemo(
    () => [
      {
        id: "bolinha_status",
        Header: () => (
          <div
            className="sticky left-0 z-20 bg-transparent flex justify-center items-center h-10 text-xl text-gray-700"
            title="Status da lead com base nas tarefas e interações"
          >
            🟢
          </div>
        ),
        Cell: ({ row }) => (
          <StatusBolinhaCell
            lead={row.original}
            tarefasPorLead={tarefasPorLead}
            interacoesPorLead={interacoesPorLead}
          />
        ),
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: () => (
          <div className="sticky left-10 z-10 bg-transparent text-white font-semibold px-2 py-1">
            Paciente
          </div>
        ),
        accessor: "nome_lead",
        Cell: ({ value, row }) => {
          const lead = row.original;

          const transferenciaPendente =
            temTransferenciaPendente(lead?.id) ||
            Boolean(lead?._solicitacao_transferencia_pendente);

          const transferenciaConcluida =
            !transferenciaPendente && Number(lead?.transferida) === 1;

          const tituloBotao = transferenciaPendente
            ? "Transferência pendente de aprovação"
            : transferenciaConcluida
              ? "Lead já transferida"
              : "Solicitar transferência";

          const classeBotao = transferenciaPendente
            ? "bg-orange-500 hover:bg-orange-600 text-white border-orange-600"
            : transferenciaConcluida
              ? "bg-blue-600 hover:bg-blue-700 text-white border-blue-700"
              : "bg-indigo-100 hover:bg-indigo-200 text-indigo-700 border-indigo-300";

          return (
            <div className="flex items-center gap-2 max-w-[220px]">
              <button
                type="button"
                onClick={() => {
                  setLeadSelecionadaTransferencia(lead);
                  setModalTransferenciaAberto(true);
                }}
                title={tituloBotao}
                className={`shrink-0 w-7 h-7 rounded-full font-bold shadow-sm border transition ${classeBotao}`}
              >
                ⇄
              </button>
              <span className="text-sm font-medium text-blue-800 truncate block whitespace-nowrap">
                {value}
              </span>
            </div>
          );
        },
      },
      {
        id: "selecao_exportacao",
        Header: () => (
          <label
            className="flex flex-col items-center justify-center gap-0.5 cursor-pointer select-none text-white"
            title="Selecionar ou desmarcar todas as leads filtradas"
          >
            <input
              type="checkbox"
              checked={todasLeadsFiltradasSelecionadas}
              onChange={alternarSelecaoTodasLeadsFiltradas}
              disabled={idsLeadsFiltradas.length === 0}
              className="w-4 h-4 accent-sky-500 cursor-pointer disabled:cursor-not-allowed"
              aria-label="Selecionar todas as leads filtradas para seleção"
            />
            <span className="text-[9px] leading-none">Selecionar</span>
          </label>
        ),
        Cell: ({ row }) => {
          const leadId = String(row.original?.id ?? "").trim();
          const selecionada = leadsSelecionadasIds.has(leadId);

          return (
            <div className="flex justify-center">
              <input
                type="checkbox"
                checked={selecionada}
                onChange={() => alternarSelecaoLead(leadId)}
                className="w-4 h-4 accent-sky-600 cursor-pointer"
                aria-label={`Selecionar ${row.original?.nome_lead || "lead"} para seleção`}
              />
            </div>
          );
        },
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: () => <FaWhatsapp size={22} color="#25d366" title="WhatsApp" />,
        id: "whatsapp",
        Cell: ({ row }) => (
          <WhatsAppCell
            lead={row.original}
            badgesWhatsapp={badgesWhatsapp}
            onOpen={async (lead) => {
              const empresaIdLocal = lead.empresa_id || empresaAtiva?.id;
              if (!empresaIdLocal) return;

              setLeadChatAberto({ ...lead, empresa_id: empresaIdLocal });

              try {
                await fetch("/api/whatsapp/marcar-lidas", {
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify({
                    lead_id: lead.id,
                    empresa_id: empresaIdLocal,
                  }),
                });

                setBadgesWhatsapp((prev) => ({
                  ...prev,
                  [lead.id]: { count: 0, status: null },
                }));
              } catch (err) {
                console.error("❌ Erro ao marcar mensagens como lidas:", err);
              }
            }}
          />
        ),
        disableSortBy: true,
        disableFilters: true,
      },
      {
        Header: "Telefone",
        accessor: "telefone1",
        Cell: ({ value, row }) => {
          const lead = row.original;
          const temObservacao = Boolean(
            String(lead?.observacao_paciente || "").trim(),
          );

          return (
            <div className="flex items-center gap-2 max-w-[155px] whitespace-nowrap">
              <span className="text-sm truncate block max-w-[120px]">
                {value || "—"}
              </span>

              <button
                type="button"
                title={
                  temObservacao
                    ? "Ver/editar observação do paciente"
                    : "Adicionar observação do paciente"
                }
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  abrirModalObservacaoPaciente(lead);
                }}
                className={`shrink-0 inline-flex items-center justify-center w-7 h-7 rounded-full border transition shadow-sm ${
                  temObservacao
                    ? "bg-blue-50 border-blue-300 text-blue-700 hover:bg-blue-100"
                    : "bg-slate-50 border-slate-200 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
                }`}
              >
                <StickyNote className="w-4 h-4" />
              </button>
            </div>
          );
        },
      },
      {
        Header: "Procedimento",
        accessor: "id_procedimento",
        Cell: ({ value, row }) => {
          const lead = row?.original || {};
          const leadManutencao = isLeadManutencaoAparelho(lead);
          const nomeProcedimento = getNomeById(value, procedimentos) || "—";

          return (
            <span
              title={
                leadManutencao
                  ? "Lead de Manutenção / carteira ortodôntica"
                  : nomeProcedimento
              }
              className="text-sm truncate max-w-[140px] block whitespace-nowrap"
            >
              {nomeProcedimento}
            </span>
          );
        },
      },
      {
        Header: "Potencial",
        accessor: "potencial",
        Cell: ({ value }) => {
          const cores = {
            Alto: "bg-green-500",
            Médio: "bg-yellow-400",
            Baixo: "bg-pink-400",
          };
          return (
            <span
              className={`text-sm px-2 py-1 rounded-md font-medium text-white shadow-sm truncate max-w-[110px] block whitespace-nowrap ${
                cores[value] || "bg-gray-400"
              }`}
            >
              {value || "—"}
            </span>
          );
        },
      },
      {
        Header: "Total",
        accessor: "total_orcamento",
        Cell: ({ value }) => (
          <span className="text-blue-600 font-semibold text-sm">
            {Number(value || 0).toLocaleString("pt-BR", {
              style: "currency",
              currency: "BRL",
            })}
          </span>
        ),
      },
      {
        Header: "Mín.",
        accessor: "valor_minimo",
        Cell: ({ row }) => (
          <InlineValorMinimoCell
            lead={row.original}
            onSave={salvarValorMinimoDaTabela}
          />
        ),
        disableFilters: true,
      },
      {
        Header: "Fechado",
        id: "fechado_recebido",
        accessor: (row) =>
          somarRecebimentosLead(recebPorLead.get(String(row?.id)) || []),
        Cell: ({ row }) => {
          const recebimentoBloqueado = !podeLancarRecebimento(row.original);
          const totalRecebidoLead = somarRecebimentosLead(
            recebPorLead.get(String(row.original?.id)) || [],
          );

          return (
            <div className="flex items-center justify-between gap-1">
              <span className="text-green-600 font-semibold text-sm">
                {moeda(totalRecebidoLead)}
              </span>
              <button
                onClick={() => abrirModalRecebimento(row.original)}
                aria-disabled={recebimentoBloqueado}
                className={`p-1 rounded transition ${
                  recebimentoBloqueado
                    ? "cursor-not-allowed opacity-50 bg-slate-100"
                    : "hover:bg-green-100"
                }`}
                title={
                  recebimentoBloqueado
                    ? "Altere a negociação para Fechado ou Fechado Parcial antes de lançar o recebimento"
                    : isLeadManutencaoAparelho(row.original)
                      ? "Registrar recebimento de Manutenção"
                      : "Registrar recebimento"
                }
                type="button"
              >
                <HandCoins className="text-green-600 w-5 h-5" />
              </button>
            </div>
          );
        },
      },
      {
        Header: "Status",
        id: "status_agendamento_periodo",
        Cell: ({ row }) => {
          const ag = row.original._ultimo_agendamento;
          const valor = ag?.status_agendamento || "";
          const cores = {
            Agendado: "bg-blue-500",
            Reagendado: "bg-indigo-500",
            Compareceu: "bg-green-500",
            Faltou: "bg-red-500",
            Pendente: "bg-yellow-500",
            "Sem Contato": "bg-orange-500",
          };
          const alias = {
            Comparecimento: "Compareceu",
            Pendência: "Pendente",
          };
          const label = alias[valor] || valor || "—";
          const style = cores[label] || "bg-zinc-400";

          return (
            <span
              className={`px-3 py-1 rounded-md text-sm font-medium text-white shadow-sm ${style}`}
            >
              {label}
            </span>
          );
        },
      },
      {
        Header: "Agendado",
        id: "data_agendamento_periodo",
        Cell: ({ row }) => {
          const value = row.original._ultimo_agendamento?.data_agendamento;

          return (
            <div className="flex items-center gap-2 max-w-[120px]">
              {value ? (
                <span className="text-sm text-green-600 font-semibold truncate block whitespace-nowrap">
                  {value.slice(0, 10).split("-").reverse().join("/")}
                </span>
              ) : (
                <span className="text-sm text-slate-400 italic block whitespace-nowrap">
                  —
                </span>
              )}
              <button
                className="ml-1 text-blue-500 hover:text-blue-700"
                title="Agendar ou editar agendamento"
                onClick={() => {
                  setModalAgendamentoAberto(true);
                  setAgendamentoSelecionado(row.original);
                }}
                style={{
                  background: "none",
                  border: "none",
                  padding: 0,
                  cursor: "pointer",
                }}
                type="button"
              >
                <svg
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <circle cx="12" cy="12" r="10" />
                  <path d="M12 6v6l4 2" />
                </svg>
              </button>
            </div>
          );
        },
      },
      {
        Header: "Negociação",
        accessor: "status_negociacao",
        Cell: ({ value, row }) => {
          const lead = row.original;
          const label = (value ?? "—").toString().trim() || "—";
          const cores = {
            Fechado: "bg-green-500",
            "Fechado Parcial": "bg-emerald-400",
            Perdido: "bg-red-500",
            Negociando: "bg-yellow-400",
            Pendente: "bg-slate-400",
            "Migrou Concorrência": "bg-orange-500",
          };
          const cor = cores[label] || "bg-zinc-400";
          const empresaIdLocal = lead.empresa_id ?? usuarioLogado?.empresa_id;

          const fechadoOuParcial = ["fechado", "fechado parcial"].includes(
            norm(label),
          );
          const dataFechamento = lead?.data_fechamento
            ? String(lead.data_fechamento).slice(0, 10)
            : "";
          const dataFechamentoBr = dataFechamento
            ? dataFechamento.split("-").reverse().join("/")
            : "";

          return (
            <div className="flex items-center gap-2">
              <CadeadoLead
                leadId={lead.id}
                cadeado={lead.cadeado}
                usuarioAtual={usuarioLogado}
                empresaId={empresaIdLocal}
                onStatusChange={(novo) =>
                  setLeads((arr) =>
                    arr.map((l) =>
                      l.id === lead.id ? { ...l, cadeado: novo } : l,
                    ),
                  )
                }
              />

              {fechadoOuParcial && (
                podeGerenciarDataFechamento ? (
                  <button
                    type="button"
                    onClick={() => abrirModalDataFechamento(lead)}
                    className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border transition ${
                      dataFechamento
                        ? "border-blue-200 bg-blue-50 text-blue-700 hover:border-blue-300 hover:bg-blue-100"
                        : "border-amber-300 bg-amber-50 text-amber-700 hover:border-amber-400 hover:bg-amber-100"
                    }`}
                    title={
                      dataFechamento
                        ? `Fechado em ${dataFechamentoBr} · clique para consultar/corrigir`
                        : "Data de fechamento não informada · clique para informar"
                    }
                    aria-label={
                      dataFechamento
                        ? `Fechado em ${dataFechamentoBr}`
                        : "Informar data de fechamento"
                    }
                  >
                    <CalendarCheck className="h-4 w-4" />
                  </button>
                ) : (
                  <span
                    className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-md border ${
                      dataFechamento
                        ? "border-blue-200 bg-blue-50 text-blue-700"
                        : "border-amber-200 bg-amber-50 text-amber-700"
                    }`}
                    title={
                      dataFechamento
                        ? `Fechado em ${dataFechamentoBr}`
                        : "Data de fechamento não informada"
                    }
                    aria-label={
                      dataFechamento
                        ? `Fechado em ${dataFechamentoBr}`
                        : "Data de fechamento não informada"
                    }
                  >
                    <CalendarCheck className="h-4 w-4" />
                  </span>
                )
              )}

              <span
                className={`px-3 py-1 rounded-md text-sm font-medium text-white shadow-sm ${cor}`}
              >
                {label}
              </span>
            </div>
          );
        },
      },
      {
        id: "tarefas",
        Header: () => (
          <div className="flex justify-center pt-[10px]">
            <CalendarCheck
              className="w-5 h-5 text-emerald-600"
              title="Tarefas da Lead"
            />
          </div>
        ),
        Cell: ({ row }) => (
          <div className="flex justify-center">
            <CalendarCheck
              onClick={async () => {
                try {
                  contextoSorrisoTarefaRef.current = null;
                  idsTarefasAntesSorrisoRef.current = new Set();
                  setLeadSelecionada(row.original);
                  setModalAberto(true);
                  const empresa_id =
                    empresaAtiva?.id ?? usuarioLogado?.empresa_id;

                  if (!empresa_id) {
                    setTarefasDaLead([]);
                    return;
                  }

                  const res = await api.get("/tarefas", {
                    params: { lead_id: row.original.id, empresa_id },
                  });
                  const tarefas = Array.isArray(res?.data) ? res.data : [];
                  setTarefasDaLead(tarefas);
                } catch (error) {
                  console.error("❌ Erro ao carregar tarefas da lead:", error);
                  setTarefasDaLead([]);
                }
              }}
              className="w-5 h-5 text-emerald-600 hover:text-emerald-800 transition cursor-pointer"
              title="Gerenciar tarefas"
            />
          </div>
        ),
        disableFilters: true,
      },
      {
        id: "interacoes",
        Header: () => (
          <div className="flex justify-center pt-[10px]">
            <PhoneCall
              className="w-5 h-5 text-yellow-500"
              title="Interações da Lead"
            />
          </div>
        ),
        Cell: ({ row }) => (
          <div className="flex justify-center">
            <PhoneCall
              onClick={() => {
                contextoSorrisoInteracaoRef.current = null;
                idsInteracoesAntesSorrisoRef.current = new Set();
                setLeadSelecionadaParaInteracao(row.original);
                setInteracoesModalAberto(true);
              }}
              className="w-5 h-5 text-yellow-500 hover:text-yellow-700 transition cursor-pointer"
              title="Ver interações"
            />
          </div>
        ),
        disableFilters: true,
      },
      {
        id: "acoes",
        Header: () => (
          <div
            className="sticky right-0 z-20 flex justify-center items-center h-10"
            title="Ações da lead"
          >
            <Target className="w-5 h-5 text-white" />
          </div>
        ),
        Cell: ({ row }) => (
          <DropdownAcoes
            lead={row.original}
            onEditar={() => {
              const lead = row.original;
              const bloqueadaPorTransferencia =
                temTransferenciaPendente(lead?.id) ||
                Boolean(lead?._solicitacao_transferencia_pendente);

              if (bloqueadaPorTransferencia) {
                toast.warning(
                  "Esta lead está com transferência pendente de aprovação e não pode ser editada.",
                );
                return;
              }

              setForm({
                data_lead: lead.data_lead
                  ? new Date(lead.data_lead).toISOString().split("T")[0]
                  : "",
                nome_lead: lead.nome_lead || "",
                telefone1: lead.telefone1 || "",
                total_orcamento: lead.total_orcamento
                  ? `R$ ${parseFloat(lead.total_orcamento)
                      .toFixed(2)
                      .replace(".", ",")}`
                  : "",
                total_contratado: lead.total_contratado
                  ? `R$ ${parseFloat(lead.total_contratado)
                      .toFixed(2)
                      .replace(".", ",")}`
                  : "",
                data_agendamento: lead.data_agendamento?.slice(0, 10) || "",
                id_procedimento: lead.id_procedimento || "",
                id_regiao: lead.id_regiao || "",
                id_usuario: lead.id_usuario || "",
                id_dentista: lead.id_dentista || "",
                id_fonte_lead: lead.id_fonte_lead || "",
                empresa_id: lead.empresa_id || "",
                status_agendamento: lead.status_agendamento || "",
                potencial: lead.potencial || "",
                id_campanha: lead.id_campanha || "",
                usuario_criador_id: lead.usuario_criador_id || "",
                status_negociacao: lead.status_negociacao || "",
              });
              setEditandoId(lead.id);
              setMostrarFormulario(true);
            }}
            onExcluir={async () => {
              const lead = row.original;
              const bloqueadaPorTransferencia =
                temTransferenciaPendente(lead?.id) ||
                Boolean(lead?._solicitacao_transferencia_pendente);

              if (bloqueadaPorTransferencia) {
                toast.warning(
                  "Esta lead está com transferência pendente de aprovação e não pode ser excluída.",
                );
                return;
              }

              if (!window.confirm("Tem certeza que deseja excluir esta lead?"))
                return;

              try {
                await api.delete(`/leads/${row.original.id}`);
                toast.success("Lead excluída com sucesso!");
                await refreshLeads();
              } catch (err) {
                console.error("Erro ao excluir lead:", err);
                toast.error("Erro ao excluir lead.");
              }
            }}
            onVisualizarLogs={(lead) => {
              setLeadSelecionadaLogs(lead);
              setMostrarLogsModal(true);
            }}
          >
            <Target className="w-5 h-5 text-blue-600" />
          </DropdownAcoes>
        ),
        disableFilters: true,
        disableSortBy: true,
      },
      {
        Header: "Captador",
        accessor: "usuario_criador_id",
        Cell: ({ value }) => getPrimeiroNomeById(value, usuarios),
      },
      {
        Header: "Data",
        accessor: "data_lead",
        Cell: ({ value }) => {
          if (!value) return "";
          const [year, month, day] = value.split("-");
          const date = new Date(Number(year), Number(month) - 1, Number(day));
          return date.toLocaleDateString();
        },
      },
      {
        Header: "Região",
        accessor: "id_regiao",
        Cell: ({ value }) => getNomeById(value, regioes),
      },
      {
        Header: "Dentista",
        accessor: "id_dentista",
        Cell: ({ value }) => getNomeById(value, dentistas),
      },
      {
        Header: "Fonte",
        accessor: "id_fonte_lead",
        Cell: ({ value }) => (
          <span className="text-sm truncate max-w-[140px] block whitespace-nowrap">
            {getNomeById(value, fontes)}
          </span>
        ),
      },
      {
        Header: "Campanha",
        accessor: "nome_campanha",
        Cell: ({ value }) => (
          <span className="text-sm text-gray-800 truncate block max-w-[160px] whitespace-nowrap">
            {value || "—"}
          </span>
        ),
      },
      {
        Header: "Lançamento",
        accessor: "criado_em",
        Cell: ({ value }) =>
          value ? new Date(value).toLocaleDateString("pt-BR") : "—",
      },
    ],
    [
      tarefasPorLead,
      interacoesPorLead,
      badgesWhatsapp,
      recebPorLead,
      procedimentos,
      regioes,
      dentistas,
      fontes,
      usuarios,
      getNomeById,
      abrirModalObservacaoPaciente,
      abrirModalDataFechamento,
      podeGerenciarDataFechamento,
      empresaAtiva?.id,
      usuarioLogado,
      refreshLeads,
      solicitacaoPendentePorLead,
      temTransferenciaPendente,
      salvarValorMinimoDaTabela,
      leadsSelecionadasIds,
      todasLeadsFiltradasSelecionadas,
      alternarSelecaoTodasLeadsFiltradas,
      alternarSelecaoLead,
      idsLeadsFiltradas.length,
    ],
  );

  const defaultColumn = useMemo(
    () => ({
      Filter: DefaultColumnFilter,
    }),
    [],
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    useTable(
      {
        columns,
        data: leadsOrdenadas,
        defaultColumn,
        autoResetSortBy: false,
        autoResetFilters: false,
      },
      useFilters,
      useSortBy,
    );

  const virtualEnabled = rows.length >= VIRTUAL_MIN_ROWS;
  const totalVirtualHeight = rows.length * VIRTUAL_ROW_HEIGHT;

  const visibleWindow = useMemo(() => {
    if (!virtualEnabled) {
      return {
        startIndex: 0,
        endIndex: rows.length,
        visibleRows: rows,
        topSpacer: 0,
        bottomSpacer: 0,
      };
    }

    const startIndex = Math.max(
      0,
      Math.floor(virtualScrollTop / VIRTUAL_ROW_HEIGHT) - VIRTUAL_OVERSCAN,
    );

    const visibleCount =
      Math.ceil(virtualViewportHeight / VIRTUAL_ROW_HEIGHT) +
      VIRTUAL_OVERSCAN * 2;

    const endIndex = Math.min(rows.length, startIndex + visibleCount);

    return {
      startIndex,
      endIndex,
      visibleRows: rows.slice(startIndex, endIndex),
      topSpacer: startIndex * VIRTUAL_ROW_HEIGHT,
      bottomSpacer: Math.max(
        0,
        totalVirtualHeight - endIndex * VIRTUAL_ROW_HEIGHT,
      ),
    };
  }, [
    rows,
    virtualEnabled,
    virtualScrollTop,
    virtualViewportHeight,
    totalVirtualHeight,
  ]);

  const handleTableScroll = useCallback((e) => {
    const nextTop = e.currentTarget.scrollTop;
    if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);

    scrollRafRef.current = requestAnimationFrame(() => {
      setVirtualScrollTop(nextTop);
    });
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;

    const syncSize = () => {
      setVirtualViewportHeight(el.clientHeight || 600);
      setVirtualScrollTop(el.scrollTop || 0);
    };

    syncSize();

    if (typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(syncSize);
    ro.observe(el);

    return () => ro.disconnect();
  }, [rows.length]);

  useEffect(() => {
    return () => {
      if (scrollRafRef.current) cancelAnimationFrame(scrollRafRef.current);
    };
  }, []);

  useEffect(() => {
    if (!tbodyRef.current) return;

    const paintRows = virtualEnabled
      ? visibleWindow.visibleRows.length
      : rows.length;

    const m = appMarkStart("leadsv3:paint:tbody", {
      rows: paintRows,
      totalRows: rows.length,
    });

    const raf = requestAnimationFrame(() => {
      try {
        void tbodyRef.current.getBoundingClientRect();
      } finally {
        if (m) appMarkEnd(m);
      }
    });

    return () => cancelAnimationFrame(raf);
  }, [rows.length, virtualEnabled, visibleWindow.visibleRows.length]);

  useEffect(() => {
    if (leadSelecionadaParaInteracao?.id) {
      const leadId = String(leadSelecionadaParaInteracao.id);
      setInteracoesDaLead(
        (interacoes || []).filter(
          (i) => String(i.id_lead ?? i.lead_id) === leadId,
        ),
      );
    } else {
      setInteracoesDaLead([]);
    }
  }, [interacoes, leadSelecionadaParaInteracao]);

  const handleLimparFiltros = () => {
    setFiltroPotencial("");
    setFiltroStatus([]);
    setFiltroDentista("");
    setFiltroFonte("");
    setFiltroCaptador("");
    setFiltroRegiao("");
    setDataInicio("");
    setDataFim("");
    setFiltroCampanha("");
    setFiltroStatusNegociacao([]);
    setFiltroProcedimento("");
    setFiltroDataAgendamento("");
    setFiltroPendenciasHoje(false);
    setFiltroTarefasAtrasadas(false);
    setFiltroSemTarefa(false);
    setFiltroOrcamentoSemOportunidade(false);
    setSoMeusLeads(false);
    setNomeUI("");
    setTelUI("");
    setFiltroLeadIdUrl("");
    setFiltroUrlAtivo(false);

    if (typeof window !== "undefined" && window.location.search) {
      window.history.replaceState(
        {},
        "",
        `${window.location.pathname}${window.location.hash || ""}`,
      );
    }
  };

  const preencherFormularioComLeadExistente = useCallback((lead) => {
    if (!lead) return;

    setForm({
      data_lead: lead.data_lead
        ? new Date(lead.data_lead).toISOString().split("T")[0]
        : "",
      nome_lead: lead.nome_lead || "",
      telefone1: lead.telefone1 || "",
      total_orcamento: lead.total_orcamento
        ? `R$ ${parseFloat(lead.total_orcamento).toFixed(2).replace(".", ",")}`
        : "",
      total_contratado: lead.total_contratado
        ? `R$ ${parseFloat(lead.total_contratado).toFixed(2).replace(".", ",")}`
        : "",
      data_agendamento: lead.data_agendamento?.slice(0, 10) || "",
      id_procedimento: lead.id_procedimento || "",
      id_regiao: lead.id_regiao || "",
      id_usuario: lead.id_usuario || "",
      id_dentista: lead.id_dentista || "",
      id_fonte_lead: lead.id_fonte_lead || "",
      empresa_id: lead.empresa_id || "",
      status_agendamento: lead.status_agendamento || "",
      potencial: lead.potencial || "",
      id_campanha: lead.id_campanha || "",
      usuario_criador_id: lead.usuario_criador_id || "",
      status_negociacao: lead.status_negociacao || "",
    });

    setEditandoId(lead.id);
    setMostrarFormulario(true);
    setDuplicidadeTelefoneAviso(null);
    toast.info("Lead existente aberta para conferência/recuperação.");
  }, []);

  const handleInserirLead = () => {
    setMostrarFormulario(true);
    setEditandoId(null);
    setDuplicidadeTelefoneAviso(null);
    ultimaConsultaDuplicidadeRef.current = "";

    startTransition(() => {
      setForm({
        data_lead: "",
        nome_lead: "",
        telefone1: "",
        total_orcamento: "",
        total_contratado: "",
        data_agendamento: "",
        id_procedimento: "",
        id_regiao: "",
        id_usuario: usuarioLogado?.id || "",
        usuario_criador_id: "",
        id_dentista: "",
        id_fonte_lead: "",
        empresa_id: usuarioLogado?.empresa_id || "",
        status_agendamento: "",
        potencial: "",
        status_negociacao: "",
        id_campanha: "",
      });
    });
  };

  useEffect(() => {
    if (!mostrarFormulario) {
      setDuplicidadeTelefoneAviso(null);
      ultimaConsultaDuplicidadeRef.current = "";
      return;
    }

    const tel = normalizarTelefoneLead(form.telefone1);

    if (tel.length < 11) {
      setDuplicidadeTelefoneAviso(null);
      ultimaConsultaDuplicidadeRef.current = "";
      return;
    }

    const empresaIdConsulta =
      empresaAtiva?.id || form.empresa_id || usuarioLogado?.empresa_id || null;

    if (!empresaIdConsulta) return;

    const assinatura = [
      empresaIdConsulta,
      tel,
      normalizarNomeLead(form.nome_lead || ""),
      form.id_procedimento || "sem-procedimento",
      editandoId || "novo",
    ].join("|");

    if (ultimaConsultaDuplicidadeRef.current === assinatura) return;
    ultimaConsultaDuplicidadeRef.current = assinatura;

    let cancelado = false;

    const timer = setTimeout(async () => {
      try {
        const res = await api.get(
          `/leads?empresa_id=${empresaIdConsulta}&mostrarInativas=true`,
        );

        if (cancelado) return;

        const lista = Array.isArray(res)
          ? res
          : Array.isArray(res?.data)
            ? res.data
            : res?.data?.leads || [];

        setTodasLeads(Array.isArray(lista) ? lista : []);

        const encontrados = (lista || [])
          .filter(
            (l) =>
              String(l?.empresa_id || empresaIdConsulta) ===
              String(empresaIdConsulta),
          )
          .filter((l) => normalizarTelefoneLead(l?.telefone1) === tel)
          .filter((l) => String(l?.id || "") !== String(editandoId || ""));

        if (encontrados.length === 0) {
          setDuplicidadeTelefoneAviso(null);
          return;
        }

        const classificados = encontrados.map((lead) => {
          const nomeParecido = nomesParecidosLead(
            form.nome_lead,
            lead?.nome_lead,
          );
          const mesmoProcedimento =
            String(lead?.id_procedimento || "") &&
            String(form.id_procedimento || "") &&
            String(lead?.id_procedimento || "") ===
              String(form.id_procedimento || "");
          const procedimentoAusente =
            !lead?.id_procedimento || !form.id_procedimento;
          const inativa = isLeadInativaOuPerdida(lead);

          let tipo = "familiar";
          let titulo = "Mesmo telefone em outra pessoa";
          let orientacao =
            "Pode ser familiar/responsável. Confirme antes de continuar.";

          if (
            !inativa &&
            nomeParecido &&
            (mesmoProcedimento || procedimentoAusente)
          ) {
            tipo = "bloqueio";
            titulo = "Possível duplicidade ativa";
            orientacao =
              "Provável mesma pessoa. Abra a lead existente antes de criar outra.";
          } else if (inativa && nomeParecido) {
            tipo = "recuperar";
            titulo = "Lead inativa/perdida encontrada";
            orientacao =
              "Confira o histórico. O ideal pode ser recuperar a lead antiga.";
          } else if (!inativa && nomeParecido) {
            tipo = "nova-oportunidade";
            titulo = "Mesmo paciente com possível nova oportunidade";
            orientacao =
              "Se for outro procedimento/oportunidade, pode continuar com cuidado.";
          }

          return {
            lead,
            nomeParecido,
            mesmoProcedimento,
            procedimentoAusente,
            inativa,
            tipo,
            titulo,
            orientacao,
          };
        });

        const temCritico = classificados.some((x) => x.tipo === "bloqueio");
        const temInativa = classificados.some((x) => x.tipo === "recuperar");

        setDuplicidadeTelefoneAviso({
          telefone: tel,
          total: classificados.length,
          temCritico,
          temInativa,
          itens: classificados,
        });

        toast.warning(
          temCritico
            ? "⚠️ Telefone já existe em possível duplicidade ativa. Confira antes de continuar."
            : temInativa
              ? "⚠️ Telefone encontrado em lead inativa/perdida. Confira o histórico."
              : "⚠️ Telefone já usado em outra lead. Pode ser familiar/responsável.",
        );
      } catch (err) {
        if (!cancelado) {
          console.warn(
            "⚠️ Falha ao validar duplicidade ao digitar telefone:",
            err,
          );
        }
      }
    }, 350);

    return () => {
      cancelado = true;
      clearTimeout(timer);
    };
  }, [
    mostrarFormulario,
    form.telefone1,
    form.nome_lead,
    form.id_procedimento,
    form.empresa_id,
    editandoId,
    empresaAtiva?.id,
    usuarioLogado?.empresa_id,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "usuario_criador_id" && editandoId) {
      toast.info(
        "O captador não pode ser alterado por edição. Use o módulo de transferência.",
      );
      return;
    }

    if (name === "data_lead") {
      const hoje = new Date().toISOString().split("T")[0];
      if (value > hoje) {
        setErros((prev) => ({
          ...prev,
          data_lead: "A data não pode ser no futuro.",
        }));
        return;
      }
      setErros((prev) => ({ ...prev, data_lead: null }));
      setForm((prev) => ({ ...prev, [name]: value }));
      return;
    }

    if (name === "telefone1") {
      const onlyNumbers = value.replace(/\D/g, "").slice(0, 11);
      const formatted = onlyNumbers
        .replace(/^(\d{2})(\d)/, "($1) $2")
        .replace(/(\d{5})(\d)/, "$1-$2");

      setForm((prev) => ({ ...prev, [name]: formatted }));
      setErros((prev) => ({
        ...prev,
        telefone1:
          onlyNumbers.length !== 11
            ? "Telefone deve ter 11 dígitos (DDD + 9 + número)"
            : null,
      }));
      return;
    }

    if (name === "total_orcamento" || name === "total_contratado") {
      const digits = value.replace(/\D/g, "");
      if (!digits) {
        setForm((prev) => ({ ...prev, [name]: "" }));
        return;
      }
      const cents = parseInt(digits, 10);
      const numeric = (cents / 100).toFixed(2);
      const formatted = "R$ " + numeric.replace(".", ",");
      setForm((prev) => ({ ...prev, [name]: formatted }));
      return;
    }

    if (name === "nome_lead") {
      const formatado = value
        .toLowerCase()
        .replace(/\b\w/g, (char) => char.toUpperCase());
      setForm((prev) => ({ ...prev, [name]: formatado }));
      return;
    }

    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();

    const leadEditandoAtual =
      (leads || []).find((l) => String(l.id) === String(editandoId)) ||
      (todasLeads || []).find((l) => String(l.id) === String(editandoId)) ||
      null;

    const bloqueadaPorTransferenciaEdicao =
      Boolean(editandoId) &&
      (temTransferenciaPendente(editandoId) ||
        Boolean(leadEditandoAtual?._solicitacao_transferencia_pendente));

    if (bloqueadaPorTransferenciaEdicao) {
      toast.warning(
        "Esta lead está com transferência pendente de aprovação e não pode ser editada.",
      );
      return;
    }

    const telefoneValido = (telefone) => {
      if (!telefone) return false;
      const apenasNumeros = String(telefone).replace(/\D/g, "");
      return apenasNumeros.length === 11;
    };

    const usuarioLogadoLS = (() => {
      try {
        return JSON.parse(localStorage.getItem("usuarioLogado") || "{}");
      } catch {
        return {};
      }
    })();

    const empresaAtivaLS = (() => {
      try {
        return JSON.parse(localStorage.getItem("empresaAtiva") || "{}");
      } catch {
        return {};
      }
    })();

    const isEdit = Boolean(editandoId);
    const endpoint = isEdit ? `/leads/${editandoId}` : "/leads";

    const empresaIdFinalRaw =
      form.empresa_id ||
      empresaAtiva?.id ||
      empresaAtivaLS?.id ||
      usuarioLogadoLS?.empresa_id;

    const empresaIdFinal = Number(empresaIdFinalRaw);

    if (!Number.isFinite(empresaIdFinal) || empresaIdFinal <= 0) {
      toast.error("Selecione a empresa antes de salvar a lead.");
      return;
    }

    const isEmpty = (v) =>
      v == null || (typeof v === "string" && v.trim() === "");

    const clean = (obj) =>
      Object.fromEntries(
        Object.entries(obj).filter(
          ([, v]) => !(v == null || (typeof v === "string" && v.trim() === "")),
        ),
      );

    const toNum = (v) => (isEmpty(v) ? null : Number(v));

    const toMoney = (v) => {
      if (v == null || v === "") return null;
      if (typeof v === "number") return Number.isFinite(v) ? v : null;

      const s = String(v)
        .replace(/\s|R\$\s?/g, "")
        .trim();

      if (/^[\d.]+,\d{2}$/.test(s)) {
        return Number(s.replace(/\./g, "").replace(",", "."));
      }

      const n = Number(s.replace(/[^\d.\-]/g, ""));
      return Number.isFinite(n) ? n : null;
    };

    const normDate = (d) => {
      if (!d) return null;
      let dt = null;

      if (d instanceof Date && !isNaN(d)) {
        dt = d;
      } else {
        const s = String(d).trim();

        const br = s.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
        if (br) dt = new Date(+br[3], +br[2] - 1, +br[1], 0, 0, 0, 0);

        if (!dt) {
          const ymdH = s.match(
            /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?$/,
          );
          if (ymdH) {
            dt = new Date(
              +ymdH[1],
              +ymdH[2] - 1,
              +ymdH[3],
              +ymdH[4],
              +ymdH[5],
              +(ymdH[6] || 0),
              0,
            );
          }
        }

        if (!dt) {
          const ymd = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
          if (ymd) dt = new Date(+ymd[1], +ymd[2] - 1, +ymd[3], 0, 0, 0, 0);
        }

        if (!dt) {
          const iso = s.includes("T") ? s : s.replace(" ", "T");
          const tmp = new Date(iso);
          if (!isNaN(tmp)) dt = tmp;
        }
      }

      if (!dt) return null;

      const pad = (n) => String(n).padStart(2, "0");
      return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(
        dt.getDate(),
      )} ${pad(dt.getHours())}:${pad(dt.getMinutes())}:${pad(dt.getSeconds())}`;
    };

    const normalizarTel = (t) => String(t || "").replace(/\D/g, "");

    const normTxt = (s) =>
      String(s || "")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .toLowerCase()
        .trim();

    const isPerdidaOuConcorrencia = (statusNeg) => {
      const s = normTxt(statusNeg);
      return s === "perdido" || s === "migrou concorrencia";
    };

    const acharLeadExistenteLocal = () => {
      const telForm = normalizarTel(form.telefone1);
      const nomeForm = normTxt(form.nome_lead);

      const acharPorTelefone = (lista) =>
        (lista || []).find((l) => normalizarTel(l.telefone1) === telForm);

      const acharPorNomeETelefone = (lista) =>
        (lista || []).find(
          (l) =>
            normTxt(l.nome_lead) === nomeForm &&
            normalizarTel(l.telefone1) === telForm,
        );

      return (
        acharPorTelefone(leads) ||
        acharPorNomeETelefone(leads) ||
        acharPorTelefone(todasLeads) ||
        acharPorNomeETelefone(todasLeads) ||
        null
      );
    };

    const acharLeadOriginalPorId = () => {
      const id = Number(editandoId);
      return (
        (leads || []).find((l) => Number(l.id) === id) ||
        (todasLeads || []).find((l) => Number(l.id) === id) ||
        null
      );
    };

    if (!isEdit) {
      const now = new Date();
      const hoje = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(
        2,
        "0",
      )}-${String(now.getDate()).padStart(2, "0")}`;

      if (!telefoneValido(form.telefone1)) {
        toast.error(
          "⛔ O telefone deve conter exatamente 11 dígitos (DDD + 9 + número).",
        );
        return;
      }

      if (!form.data_lead) {
        toast.error("⛔ Informe a data da lead.");
        return;
      }

      if (form.data_lead > hoje) {
        toast.error("⛔ A data da lead não pode ser no futuro.");
        return;
      }

      const status = (form.status_agendamento || "").trim();
      const exigeData = ["Agendado", "Reagendado", "Compareceu"].includes(
        status,
      );

      if (exigeData && !form.data_agendamento) {
        toast.error(
          "⛔ Informe a data do agendamento para o status selecionado.",
        );
        return;
      }

      if (!form.status_negociacao?.trim()) {
        toast.error("⛔ Informe corretamente o status de negociação da lead.");
        return;
      }

      if (form.id_campanha == null || String(form.id_campanha).trim() === "") {
        toast.error("⛔ Selecione uma campanha para a lead.");
        return;
      }

      const captador = Number(form.usuario_criador_id);
      if (!Number.isInteger(captador) || captador <= 0) {
        toast.error("Selecione um captador válido.");
        return;
      }

      const carregarTodasParaDuplicidade = async () => {
        try {
          const res = await api.get(
            `/leads?empresa_id=${empresaIdFinal}&mostrarInativas=true`,
          );
          const arr = Array.isArray(res)
            ? res
            : Array.isArray(res?.data)
              ? res.data
              : res?.data?.leads || [];

          if (Array.isArray(arr) && arr.length > 0) {
            setTodasLeads(arr);
            return arr;
          }
        } catch (err) {
          console.warn(
            "⚠️ Não foi possível consultar todas as leads para duplicidade:",
            err,
          );
        }

        return [...(todasLeads || []), ...(leads || [])];
      };

      const abrirLeadExistenteParaRecuperar = (existente) => {
        setForm({
          data_lead: existente.data_lead
            ? new Date(existente.data_lead).toISOString().split("T")[0]
            : "",
          nome_lead: existente.nome_lead || "",
          telefone1: existente.telefone1 || "",
          total_orcamento: existente.total_orcamento
            ? `R$ ${parseFloat(existente.total_orcamento)
                .toFixed(2)
                .replace(".", ",")}`
            : "",
          total_contratado: existente.total_contratado
            ? `R$ ${parseFloat(existente.total_contratado)
                .toFixed(2)
                .replace(".", ",")}`
            : "",
          data_agendamento: existente.data_agendamento?.slice(0, 10) || "",
          id_procedimento: existente.id_procedimento || "",
          id_regiao: existente.id_regiao || "",
          id_usuario: existente.id_usuario || "",
          id_dentista: existente.id_dentista || "",
          id_fonte_lead: existente.id_fonte_lead || "",
          empresa_id: existente.empresa_id || "",
          status_agendamento: existente.status_agendamento || "",
          potencial: existente.potencial || "",
          id_campanha: existente.id_campanha || "",
          usuario_criador_id: existente.usuario_criador_id || "",
          status_negociacao: existente.status_negociacao || "",
        });

        setEditandoId(existente.id);
        setMostrarFormulario(true);
      };

      const todasParaValidacao = await carregarTodasParaDuplicidade();
      const telForm = normalizarTelefoneLead(form.telefone1);
      const nomeForm = form.nome_lead;

      const possiveisMesmoTelefone = (todasParaValidacao || [])
        .filter(
          (l) =>
            String(l?.empresa_id || empresaIdFinal) === String(empresaIdFinal),
        )
        .filter((l) => normalizarTelefoneLead(l?.telefone1) === telForm)
        .filter((l) => String(l?.id || "") !== String(editandoId || ""));

      const duplicidadesClassificadas = possiveisMesmoTelefone.map((l) => {
        const nomeParecido = nomesParecidosLead(nomeForm, l?.nome_lead);
        const mesmoProcedimento =
          String(l?.id_procedimento || "") &&
          String(form.id_procedimento || "") &&
          String(l?.id_procedimento || "") ===
            String(form.id_procedimento || "");
        const procedimentoAusente =
          !l?.id_procedimento || !form.id_procedimento;
        const inativa = isLeadInativaOuPerdida(l);

        return {
          lead: l,
          nomeParecido,
          mesmoProcedimento,
          procedimentoAusente,
          inativa,
        };
      });

      const ativaMesmaPessoaMesmoProcedimento = duplicidadesClassificadas.find(
        (x) =>
          !x.inativa &&
          x.nomeParecido &&
          (x.mesmoProcedimento || x.procedimentoAusente),
      );

      if (ativaMesmaPessoaMesmoProcedimento) {
        const l = ativaMesmaPessoaMesmoProcedimento.lead;
        toast.error(
          `⛔ Possível duplicidade: já existe lead ATIVA para ${l.nome_lead || "este paciente"} com este telefone${ativaMesmaPessoaMesmoProcedimento.mesmoProcedimento ? " e mesmo procedimento" : ""}. Abra a lead existente antes de cadastrar outra.`,
        );
        setTelUI(form.telefone1 || "");
        return;
      }

      const inativaMesmaPessoa = duplicidadesClassificadas.find(
        (x) => x.inativa && x.nomeParecido,
      );

      if (inativaMesmaPessoa) {
        const l = inativaMesmaPessoa.lead;
        const recuperar = window.confirm(
          `⚠️ Este telefone já existe em uma lead INATIVA/PERDIDA com nome parecido:\n\n` +
            `Paciente: ${l.nome_lead || "—"}\n` +
            `Status: ${l.status_negociacao || "—"}\n\n` +
            `Clique em OK para abrir e recuperar a lead existente.\n` +
            `Clique em Cancelar somente se for uma NOVA OPORTUNIDADE real ou outro familiar usando o mesmo telefone.`,
        );

        if (recuperar) {
          toast.info("Abrindo a lead antiga para recuperação.");
          abrirLeadExistenteParaRecuperar(l);
          return;
        }
      }

      const ativaMesmaPessoaOutroProcedimento = duplicidadesClassificadas.find(
        (x) => !x.inativa && x.nomeParecido && !x.mesmoProcedimento,
      );

      if (ativaMesmaPessoaOutroProcedimento) {
        const l = ativaMesmaPessoaOutroProcedimento.lead;
        const continuar = window.confirm(
          `⚠️ Este telefone já possui uma lead ATIVA com nome parecido:\n\n` +
            `Paciente: ${l.nome_lead || "—"}\n` +
            `Status: ${l.status_negociacao || "—"}\n\n` +
            `Se for a mesma pessoa, confirme apenas se esta for uma nova oportunidade/procedimento diferente.\n` +
            `Deseja continuar?`,
        );

        if (!continuar) return;
      }

      const mesmoTelefoneNomeDiferente = duplicidadesClassificadas.filter(
        (x) => !x.nomeParecido,
      );

      if (mesmoTelefoneNomeDiferente.length > 0) {
        const resumo = mesmoTelefoneNomeDiferente
          .slice(0, 5)
          .map(
            (x) =>
              `• ${x.lead?.nome_lead || "Sem nome"} — ${x.lead?.status_negociacao || "Sem status"}`,
          )
          .join("\n");

        const continuarComoFamiliar = window.confirm(
          `⚠️ Este telefone já está cadastrado em ${mesmoTelefoneNomeDiferente.length} outra(s) lead(s):\n\n` +
            `${resumo}\n\n` +
            `Isto pode ser familiar/responsável usando o mesmo telefone.\n` +
            `Confirme somente se for OUTRA pessoa ou uma oportunidade legítima.\n\n` +
            `Deseja continuar o cadastro?`,
        );

        if (!continuarComoFamiliar) return;
      }
    }

    try {
      let payload;

      if (!isEdit) {
        const status = (form.status_agendamento || "").trim();
        const statusExigemData = ["Agendado", "Reagendado", "Compareceu"];

        const base = clean({
          empresa_id: empresaIdFinal,
          id_usuario: toNum(usuarioLogadoLS?.id),
          usuario_criador_id: toNum(form.usuario_criador_id),
          nome_lead: form.nome_lead,
          data_lead: normDate(form.data_lead),
          id_procedimento: toNum(form.id_procedimento),
          telefone1: form.telefone1,
          data_agendamento: statusExigemData.includes(status)
            ? normDate(form.data_agendamento)
            : null,
          id_regiao: toNum(form.id_regiao),
          id_dentista: toNum(form.id_dentista),
          id_fonte_lead: toNum(form.id_fonte_lead),
          id_campanha: toNum(form.id_campanha),
          status_agendamento: form.status_agendamento,
          potencial: form.potencial,
          status_negociacao: form.status_negociacao,
        });

        payload = {
          ...base,
          total_orcamento: toMoney(form.total_orcamento),
          total_contratado: toMoney(form.total_contratado),
        };

        if (
          !payload.empresa_id ||
          !payload.usuario_criador_id ||
          !payload.nome_lead ||
          !payload.data_lead ||
          !payload.id_procedimento
        ) {
          throw new Error(
            "Preencha empresa, captador, nome, data e procedimento.",
          );
        }

        await api.post(endpoint, payload);
        toast.success("Lead salva com sucesso!");
      } else {
        const leadOriginal = acharLeadOriginalPorId();

        if (!leadOriginal) {
          toast.error("Não foi possível identificar a lead original.");
          return;
        }

        const status = (form.status_agendamento || "").trim();
        const statusExigemData = ["Agendado", "Reagendado", "Compareceu"];

        payload = clean({
          empresa_id: empresaIdFinal,
          id_usuario: toNum(usuarioLogadoLS?.id),
          usuario_criador_id: toNum(leadOriginal.usuario_criador_id),
          nome_lead: form.nome_lead,
          data_lead: normDate(form.data_lead),
          id_procedimento: toNum(form.id_procedimento),
          telefone1: form.telefone1,
          total_orcamento: toMoney(form.total_orcamento),
          total_contratado: toMoney(form.total_contratado),
          data_agendamento: statusExigemData.includes(status)
            ? normDate(form.data_agendamento)
            : null,
          id_regiao: toNum(form.id_regiao),
          id_dentista: toNum(form.id_dentista),
          id_fonte_lead: toNum(form.id_fonte_lead),
          id_campanha: toNum(form.id_campanha),
          status_agendamento: form.status_agendamento,
          potencial: form.potencial,
          status_negociacao: form.status_negociacao,
        });

        if (
          !payload.empresa_id ||
          !payload.usuario_criador_id ||
          !payload.nome_lead ||
          !payload.data_lead ||
          !payload.id_procedimento
        ) {
          toast.error(
            "Campos obrigatórios ausentes: empresa, captador, nome, data e procedimento.",
          );
          return;
        }

        await api.put(endpoint, payload);
        toast.success("Lead atualizada com sucesso!");
      }

      await refreshLeads();
      await fetchSolicitacoesTransferenciaPendentes();
      if (mostrarFormulario || todasLeads.length > 0) {
        await fetchTodasLeads();
      }

      setMostrarFormulario(false);
      setEditandoId(null);

      setForm({
        data_lead: "",
        nome_lead: "",
        telefone1: "",
        total_orcamento: "",
        total_contratado: "",
        data_agendamento: "",
        id_procedimento: "",
        id_regiao: "",
        id_usuario: "",
        usuario_criador_id: "",
        id_dentista: "",
        id_fonte_lead: "",
        empresa_id: "",
        id_campanha: "",
        status_agendamento: "",
        status_negociacao: "",
        potencial: "",
      });
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;

      console.error("❌ Erro ao salvar lead:", status, data || err);

      if (status === 409) {
        const existente = acharLeadExistenteLocal();

        if (!existente) {
          toast.warning(
            "⚠️ Já existe uma lead com esse telefone. Ela pode estar PERDIDA/INATIVA. Pesquise pelo telefone e recupere editando o status de negociação.",
          );
          return;
        }

        const inativaFlag = Number(existente.inativo) === 1;
        const perdidaOuConc = isPerdidaOuConcorrencia(
          existente.status_negociacao,
        );

        if (inativaFlag || perdidaOuConc) {
          toast.warning(
            "⚠️ Lead encontrada como PERDIDA/INATIVA. Não é permitido gravar nova. Vou abrir a lead existente para RECUPERAR.",
          );

          setForm({
            data_lead: existente.data_lead
              ? new Date(existente.data_lead).toISOString().split("T")[0]
              : "",
            nome_lead: existente.nome_lead || "",
            telefone1: existente.telefone1 || "",
            total_orcamento: existente.total_orcamento
              ? `R$ ${parseFloat(existente.total_orcamento)
                  .toFixed(2)
                  .replace(".", ",")}`
              : "",
            total_contratado: existente.total_contratado
              ? `R$ ${parseFloat(existente.total_contratado)
                  .toFixed(2)
                  .replace(".", ",")}`
              : "",
            data_agendamento: existente.data_agendamento?.slice(0, 10) || "",
            id_procedimento: existente.id_procedimento || "",
            id_regiao: existente.id_regiao || "",
            id_usuario: existente.id_usuario || "",
            id_dentista: existente.id_dentista || "",
            id_fonte_lead: existente.id_fonte_lead || "",
            empresa_id: existente.empresa_id || "",
            status_agendamento: existente.status_agendamento || "",
            potencial: existente.potencial || "",
            id_campanha: existente.id_campanha || "",
            usuario_criador_id: existente.usuario_criador_id || "",
            status_negociacao: existente.status_negociacao || "",
          });

          setEditandoId(existente.id);
          setMostrarFormulario(true);
          return;
        }

        toast.error(
          data?.message ||
            "⛔ Já existe uma lead ATIVA com este telefone. Não é possível gravar duplicado.",
        );
        return;
      }

      const msg =
        data?.message ||
        data?.error ||
        err?.message ||
        "Erro ao salvar lead. Verifique os campos obrigatórios.";

      toast.error(msg);
    }
  };

  const atualizarLeadNaTabela = (leadId, ultimoAgendamento) => {
    setLeads((leadsAntigos) =>
      leadsAntigos.map((lead) =>
        lead.id === leadId
          ? {
              ...lead,
              status_agendamento: ultimoAgendamento?.status_agendamento || "",
              data_agendamento: ultimoAgendamento?.data_agendamento || "",
              _ultimo_agendamento: ultimoAgendamento || null,
            }
          : lead,
      ),
    );
  };

  return (
    <>
      <div className="min-h-screen pt-0 px-1 pb-1">
        <NotificacaoTarefa tarefas={tarefas} leads={leads} />
        <ToastContainer position="bottom-right" autoClose={3001} />
        {centralDisparosAberta && (
          <CentralDisparosLeadSpeed
            empresaId={empresaId}
            leads={todasLeads?.length ? todasLeads : leads}
            selecionadosIds={leadsSelecionadasIds}
            onClose={() => setCentralDisparosAberta(false)}
          />
        )}

        {mostrarSorrisoPlanejado && (
          <SorrisoPlanejado
            leads={todasLeads.length > 0 ? todasLeads : leads}
            campanhas={campanhas}
            usuarios={usuarios}
            dentistas={dentistas}
            tarefas={tarefas}
            interacoes={interacoes}
            usuarioLogado={{
              ...(usuarios.find((u) => String(u?.id) === String(usuarioLogado?.id)) || {}),
              ...usuarioLogado,
            }}
            empresaAtiva={empresaAtiva}
            carregando={carregandoSorrisoPlanejado}
            onVoltar={() => setMostrarSorrisoPlanejado(false)}
            onNovaLead={() => {
              setMostrarSorrisoPlanejado(false);
              handleInserirLead();
            }}
            onAtualizar={async () => {
              try {
                setCarregandoSorrisoPlanejado(true);
                await Promise.all([
                  fetchTodasLeads(),
                  fetchCampanhas(),
                  carregarTarefas(),
                  carregarInteracoes(),
                ]);
              } finally {
                setCarregandoSorrisoPlanejado(false);
              }
            }}
            onWhatsApp={(lead) => {
              const empresaIdLocal = lead?.empresa_id || empresaAtiva?.id;
              if (!empresaIdLocal) {
                toast.warning("Empresa da lead não identificada.");
                return;
              }
              setLeadChatAberto({ ...lead, empresa_id: empresaIdLocal });
            }}
            onNovaTarefa={async (lead, contextoCobranca) => {
              try {
                const empresa_id = empresaAtiva?.id ?? usuarioLogado?.empresa_id;
                if (!empresa_id || !lead?.id) {
                  toast.warning("Empresa ou lead não identificada.");
                  return;
                }

                const res = await api.get("/tarefas", {
                  params: { lead_id: lead.id, empresa_id },
                });
                const lista = Array.isArray(res?.data) ? res.data : [];
                setTarefasDaLead(lista);

                contextoSorrisoTarefaRef.current = contextoCobranca
                  ? { ...contextoCobranca, lead_id: Number(lead.id) }
                  : null;
                idsTarefasAntesSorrisoRef.current = new Set(
                  lista.map((item) => String(item?.id || "")).filter(Boolean),
                );

                setLeadSelecionada(lead);
                setModalAberto(true);
              } catch (error) {
                console.error("❌ Erro ao preparar tarefa do Sorriso Planejado:", error);
                contextoSorrisoTarefaRef.current = null;
                idsTarefasAntesSorrisoRef.current = new Set();
                toast.error("Não foi possível preparar a tarefa de cobrança.");
              }
            }}
            onInteracoes={async (lead, contextoCobranca) => {
              try {
                const empresa_id = empresaAtiva?.id ?? usuarioLogado?.empresa_id;
                if (!empresa_id || !lead?.id) {
                  toast.warning("Empresa ou lead não identificada.");
                  return;
                }

                const res = await api.get("/interacoes", {
                  params: { id_lead: lead.id, empresa_id },
                });
                const lista = Array.isArray(res?.data) ? res.data : [];

                contextoSorrisoInteracaoRef.current = contextoCobranca
                  ? { ...contextoCobranca, lead_id: Number(lead.id) }
                  : null;
                idsInteracoesAntesSorrisoRef.current = new Set(
                  lista.map((item) => String(item?.id || "")).filter(Boolean),
                );

                setLeadSelecionadaParaInteracao(lead);
                setInteracoesModalAberto(true);
              } catch (error) {
                console.error("❌ Erro ao preparar interação do Sorriso Planejado:", error);
                contextoSorrisoInteracaoRef.current = null;
                idsInteracoesAntesSorrisoRef.current = new Set();
                toast.error("Não foi possível preparar a interação de cobrança.");
              }
            }}
            onLancarParcelas={(lead) => {
              toast.info(
                `Próxima etapa: lançar parcelas remanescentes de ${lead?.nome_lead || "este paciente"}.`,
              );
            }}
            onDarBaixa={(lead) => {
              toast.info(
                `A baixa de ${lead?.nome_lead || "este paciente"} será ligada ao receb_leads na etapa financeira.`,
              );
            }}
            onAbrirLead={(lead) => {
              setMostrarSorrisoPlanejado(false);
              preencherFormularioComLeadExistente(lead);
            }}
          />
        )}

        {!mostrarSorrisoPlanejado && (
        <div className="max-w-[98%] mx-auto bg-white px-2 py-2 rounded-xl shadow border border-slate-300">
          <div className="flex items-center justify-between mb-6">
            <div>
              <div className="flex items-center gap-3">
                <img
                  src={logoCapta}
                  alt="Logo CaptaCRM"
                  className="w-32 h-32 object-contain drop-shadow-md"
                />
                <div className="-ml-2">
                  <h1 className="text-3xl font-bold text-blue-800 leading-tight">
                    Gestão de Leads V3
                  </h1>
                  <p className="text-base text-blue-700 font-medium -mt-1">
                    Versão paralela para testes de performance.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex flex-col items-end gap-2">
              <button
                onClick={() => {
                  if (mostrarFormulario) {
                    setMostrarFormulario(false);
                    setEditandoId(null);
                  } else {
                    handleInserirLead();
                  }
                }}
                className={`w-48 flex items-center justify-center gap-2 ${
                  mostrarFormulario
                    ? "bg-red-600 hover:bg-red-700"
                    : "bg-gradient-to-r from-blue-900 via-blue-800 to-blue-700 hover:brightness-110"
                } text-white font-semibold px-5 py-2 rounded-md shadow-md transition-all duration-300`}
                type="button"
              >
                {mostrarFormulario ? (
                  <>
                    <Trash className="w-5 h-5" /> Cancelar
                  </>
                ) : (
                  <>
                    <PlusCircle className="w-5 h-5" /> Inserir Lead
                  </>
                )}
              </button>

              <button
                onClick={async () => {
                  setMostrarFormulario(false);
                  setEditandoId(null);
                  setMostrarSorrisoPlanejado(true);
                  try {
                    setCarregandoSorrisoPlanejado(true);
                    await Promise.all([fetchTodasLeads(), fetchCampanhas()]);
                  } finally {
                    setCarregandoSorrisoPlanejado(false);
                  }
                }}
                className="w-48 flex items-center justify-center gap-2 bg-gradient-to-r from-violet-700 via-purple-700 to-blue-700 hover:brightness-110 text-white font-semibold px-5 py-2 rounded-md shadow-md transition-all duration-300"
                type="button"
                title="Abrir carteira do Sorriso Planejado"
              >
                <HandCoins className="w-5 h-5" /> Sorriso Planejado
              </button>

              <button
                onClick={() => setMostrarInativas(!mostrarInativas)}
                className={`w-48 flex items-center justify-center gap-2 bg-white border-2 ${
                  mostrarInativas
                    ? "border-green-500 text-green-700"
                    : "border-gray-400 text-gray-600 hover:border-blue-500 hover:text-blue-800"
                } font-semibold px-5 py-2 rounded-md shadow-md transition-all duration-300`}
                type="button"
              >
                {mostrarInativas
                  ? "✅ Inativos visíveis"
                  : "🧊 Checar inativos"}
              </button>

              {mostrarFormulario && (
                <button
                  onClick={handleSubmit}
                  className="w-48 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-semibold px-5 py-2 rounded-md shadow-md transition-all duration-300"
                  type="button"
                >
                  <Save className="w-4 h-4" /> Salvar Lead
                </button>
              )}
            </div>
          </div>

          {mostrarFormulario && (
            <FormLeadWrapper
              form={form}
              setForm={setForm}
              handleChange={handleChange}
              handleSubmit={handleSubmit}
              erros={erros}
              empresas={empresas}
              campanhas={campanhas}
              procedimentos={procedimentos}
              fontes={fontes}
              regioes={regioes}
              usuarios={usuarios}
              dentistas={dentistas}
              editandoId={editandoId}
            />
          )}

          {mostrarFormulario && duplicidadeTelefoneAviso && (
            <div
              className={`mb-4 rounded-2xl border-2 p-4 shadow-lg ${
                duplicidadeTelefoneAviso.temCritico
                  ? "border-red-300 bg-red-50"
                  : duplicidadeTelefoneAviso.temInativa
                    ? "border-amber-300 bg-amber-50"
                    : "border-blue-300 bg-blue-50"
              }`}
            >
              <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-3">
                <div>
                  <div
                    className={`text-sm font-bold ${
                      duplicidadeTelefoneAviso.temCritico
                        ? "text-red-800"
                        : duplicidadeTelefoneAviso.temInativa
                          ? "text-amber-800"
                          : "text-blue-800"
                    }`}
                  >
                    ⚠️ Atenção: telefone já encontrado no histórico
                  </div>
                  <div className="text-xs text-slate-700 mt-1">
                    Este telefone aparece em {duplicidadeTelefoneAviso.total}{" "}
                    lead(s), incluindo ativas/inativas. Confira antes de perder
                    tempo preenchendo ou criar duplicidade.
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setTelUI(form.telefone1 || "");
                    setMostrarFormulario(false);
                    toast.info(
                      "Filtro por telefone aplicado para conferência.",
                    );
                  }}
                  className="shrink-0 rounded-xl bg-slate-800 px-3 py-2 text-xs font-semibold text-white shadow hover:bg-slate-900"
                >
                  Ver na lista
                </button>
              </div>

              <div className="mt-3 grid grid-cols-1 xl:grid-cols-2 gap-2">
                {duplicidadeTelefoneAviso.itens.slice(0, 6).map((item) => {
                  const lead = item.lead;
                  const status = lead?.status_negociacao || "Sem status";
                  const classe =
                    item.tipo === "bloqueio"
                      ? "border-red-200 bg-white"
                      : item.tipo === "recuperar"
                        ? "border-amber-200 bg-white"
                        : "border-blue-200 bg-white";

                  return (
                    <div
                      key={lead?.id || `${lead?.telefone1}-${lead?.nome_lead}`}
                      className={`rounded-xl border p-3 text-xs shadow-sm ${classe}`}
                    >
                      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2">
                        <div>
                          <div className="font-bold text-slate-800">
                            {lead?.nome_lead || "Sem nome"}
                          </div>
                          <div className="mt-1 text-slate-600">
                            Status: <b>{status}</b>
                            {item.inativa ? " • Inativa/Perdida" : " • Ativa"}
                          </div>
                          <div className="mt-1 text-slate-600">
                            {item.nomeParecido
                              ? "Nome parecido com o informado"
                              : "Nome diferente: pode ser familiar/responsável"}
                            {item.mesmoProcedimento
                              ? " • Mesmo procedimento"
                              : ""}
                          </div>
                          <div
                            className={`mt-2 inline-flex rounded-full px-2.5 py-1 font-semibold ${
                              item.tipo === "bloqueio"
                                ? "bg-red-100 text-red-700"
                                : item.tipo === "recuperar"
                                  ? "bg-amber-100 text-amber-700"
                                  : "bg-blue-100 text-blue-700"
                            }`}
                          >
                            {item.titulo}
                          </div>
                          <div className="mt-2 text-[11px] text-slate-500">
                            {item.orientacao}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() =>
                            preencherFormularioComLeadExistente(lead)
                          }
                          className="shrink-0 rounded-lg bg-blue-700 px-3 py-1.5 text-[11px] font-semibold text-white shadow hover:bg-blue-800"
                        >
                          Abrir lead
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <div className="w-full bg-gradient-to-r from-blue-300 via-purple-200 to-pink-200 border-2 border-blue-300 shadow-2xl rounded-2xl px-4 py-2 flex flex-col gap-2">
            <div className="flex gap-2 items-end flex-wrap">
              <FiltroDataDinamico
                tipoData={tipoDataFiltro}
                setTipoData={setTipoDataFiltro}
                dataInicio={dataInicio}
                setDataInicio={setDataInicio}
                dataFim={dataFim}
                setDataFim={setDataFim}
                erroData={erroData}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex flex-col w-[168px]">
                <label className="text-[10px] font-bold text-blue-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  🔤 Nome
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-400 text-[11px] pointer-events-none">
                    🔍
                  </span>
                  <input
                    value={nomeUI}
                    onChange={(e) => setNomeUI(e.target.value)}
                    placeholder="Filtrar por Nome"
                    type="text"
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-blue-400 text-blue-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-pink-200 transition-all duration-200 w-full text-[11px]"
                  />
                </div>
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-purple-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  📞 Telefone
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-purple-400 text-[11px] pointer-events-none">
                    📱
                  </span>
                  <input
                    value={telUI}
                    onChange={(e) => setTelUI(e.target.value)}
                    placeholder="Telefone"
                    type="text"
                    inputMode="numeric"
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-purple-400 text-purple-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all duration-200 w-full text-[11px]"
                  />
                </div>
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-pink-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  🎯 Potencial
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-pink-400 text-[11px] pointer-events-none">
                    ⭐️
                  </span>
                  <select
                    value={filtroPotencial}
                    onChange={(e) => setFiltroPotencial(e.target.value)}
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-pink-400 text-pink-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-purple-200 transition-all duration-200 w-full text-[11px]"
                  >
                    <option value="">Potencial</option>
                    <option value="Alto">⬆️ Alto</option>
                    <option value="Médio">↔️ Médio</option>
                    <option value="Baixo">⬇️ Baixo</option>
                  </select>
                </div>
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-blue-500 mb-0.5 ml-1 tracking-wider drop-shadow">
                  📌 Status
                </label>
                <FiltroStatusAgendamentoMulti
                  value={filtroStatus}
                  onChange={setFiltroStatus}
                  opcoes={statusAgendamentoOpcoes}
                />
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-pink-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  💼 Negociação
                </label>
                <FiltroNegociacaoMulti
                  value={filtroStatusNegociacao}
                  onChange={setFiltroStatusNegociacao}
                  opcoes={statusNegociacaoOpcoes}
                />
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-purple-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  🧑‍⚕️ Dentista
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-purple-400 text-[11px] pointer-events-none">
                    🧑‍⚕️
                  </span>
                  <select
                    value={filtroDentista}
                    onChange={(e) => setFiltroDentista(e.target.value)}
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-purple-400 text-purple-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all duration-200 w-full text-[11px]"
                  >
                    <option value="">Dentista</option>
                    {dentistas.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.profissional || d.nome || "—"}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-blue-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  🌐 Fonte
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-400 text-[11px] pointer-events-none">
                    🌐
                  </span>
                  <select
                    value={filtroFonte}
                    onChange={(e) => setFiltroFonte(e.target.value)}
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-blue-400 text-blue-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-pink-200 transition-all duration-200 w-full text-[11px]"
                  >
                    <option value="">Fonte</option>
                    {fontes.map((f) => (
                      <option key={f.id} value={f.id}>
                        {f.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-pink-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  🙋‍♂️ Captador
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-pink-400 text-[11px] pointer-events-none">
                    🙋‍♂️
                  </span>
                  <select
                    value={filtroCaptador}
                    onChange={(e) => {
                      setFiltroCaptador(e.target.value);
                      if (
                        String(e.target.value || "") !==
                        String(usuarioLogado?.id || "")
                      ) {
                        setSoMeusLeads(false);
                      }
                    }}
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-pink-400 text-pink-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all duration-200 w-full text-[11px]"
                  >
                    <option value="">Captador</option>
                    {usuarios.length > 0 ? (
                      usuarios.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.nome}
                        </option>
                      ))
                    ) : (
                      <option disabled>🔍 Nenhum captador encontrado</option>
                    )}
                  </select>
                </div>
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-purple-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  📍 Região
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-purple-400 text-[11px] pointer-events-none">
                    📍
                  </span>
                  <select
                    value={filtroRegiao}
                    onChange={(e) => setFiltroRegiao(e.target.value)}
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-purple-400 text-purple-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all duration-200 w-full text-[11px]"
                  >
                    <option value="">Região</option>
                    {regioes.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.regiao}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col w-[130px]">
                <label className="text-[10px] font-bold text-blue-600 mb-0.5 ml-1 tracking-wider drop-shadow">
                  📢 Campanha
                </label>
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-blue-400 text-[11px] pointer-events-none">
                    📢
                  </span>
                  <select
                    value={filtroCampanha}
                    onChange={(e) => setFiltroCampanha(e.target.value)}
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-blue-400 text-blue-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-pink-200 transition-all duration-200 w-full text-[11px]"
                  >
                    <option value="">Campanha</option>
                    {campanhas.map((camp) => (
                      <option key={camp.id} value={camp.id}>
                        {camp.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="flex flex-col w-[168px]">
                <div className="relative">
                  <span className="absolute left-2 top-1/2 -translate-y-1/2 text-pink-400 text-[11px] pointer-events-none">
                    🦷
                  </span>
                  <select
                    value={filtroProcedimento}
                    onChange={(e) => setFiltroProcedimento(e.target.value)}
                    className="pl-7 pr-2 h-8 rounded-xl border-2 border-pink-400 text-pink-700 font-semibold shadow-md focus:outline-none focus:ring-2 focus:ring-blue-200 transition-all duration-200 w-full text-[11px]"
                  >
                    <option value="">Procedimento</option>
                    {procedimentos.map((proc) => (
                      <option key={proc.id} value={proc.id}>
                        {proc.nome}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <button
                onClick={handleLimparFiltros}
                className="flex items-center justify-center gap-1 w-[130px] h-8 bg-red-100 hover:bg-red-200 text-red-700 border-2 border-red-400 rounded-xl text-[11px] shadow-md font-bold transition-all duration-200"
                style={{ minWidth: 0, minHeight: 0, lineHeight: 1 }}
                type="button"
              >
                <span style={{ fontSize: 12 }}>🧹</span>
                Limpar
              </button>

              <button
                onClick={() => {
                  localStorage.setItem(
                    "leadsParaImpressao",
                    JSON.stringify(leadsFiltradas),
                  );
                  window.open("/imprimir-leads", "_blank");
                }}
                className="flex items-center justify-center gap-1 w-[130px] h-8 bg-gray-300 hover:bg-gray-400 text-gray-900 border-2 border-gray-500 rounded-xl text-[11px] shadow-md font-bold transition-all duration-200"
                style={{ minWidth: 0, minHeight: 0, lineHeight: 1 }}
                type="button"
              >
                <span role="img" aria-label="imprimir" style={{ fontSize: 12 }}>
                  🖨️
                </span>
                Imprimir
              </button>

              <button
                onClick={() => setMostrarLegenda(!mostrarLegenda)}
                title="Ver legenda dos status"
                className="flex items-center justify-center w-[130px] h-8 bg-white border border-blue-200 rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-blue-400"
                style={{ minWidth: 0, minHeight: 0, lineHeight: 1, padding: 0 }}
                type="button"
              >
                <span
                  className="inline-block rounded-full shadow"
                  style={{
                    width: 13,
                    height: 13,
                    background:
                      "linear-gradient(135deg, #60a5fa 60%, #2563eb 100%)",
                    border: "2px solid #2563eb",
                    boxShadow: "0 0 4px #2563eb44",
                  }}
                />
                <span className="ml-2 text-[11px] text-blue-700 font-semibold">
                  Legenda
                </span>
              </button>

              <span className="flex items-center justify-center gap-1 w-[130px] h-8 bg-white border border-blue-200 text-blue-700 font-semibold rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-blue-400 select-none text-[11px]">
                <span
                  role="img"
                  aria-label="Contador"
                  className="mr-1 text-[11px]"
                >
                  🔢
                </span>
                <CountUp
                  key={leadsFiltradas.length}
                  end={leadsFiltradas.length}
                  duration={0.45}
                  decimals={0}
                  separator=""
                />
              </span>

              <label className="flex items-center gap-2 cursor-pointer select-none w-[130px] h-8 bg-white border border-green-200 rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-green-400 justify-center">
                <input
                  type="checkbox"
                  checked={soMeusLeads}
                  onChange={() => {
                    if (soMeusLeads) {
                      setFiltroCaptador("");
                      setSoMeusLeads(false);
                    } else {
                      setFiltroCaptador(String(usuarioLogado?.id || ""));
                      setSoMeusLeads(true);
                    }
                  }}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-green-400 transition-colors relative">
                  <div className="absolute left-1 top-0.5 w-4 h-4 bg-white rounded-full shadow-md peer-checked:left-5 transition-all" />
                </div>
                <span className="text-[11px] text-green-700 font-semibold transition-colors">
                  Captador
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none w-[130px] h-8 bg-white border border-yellow-200 rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-yellow-400 justify-center">
                <input
                  type="checkbox"
                  checked={filtroPendenciasHoje}
                  onChange={() => setFiltroPendenciasHoje((v) => !v)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-yellow-400 transition-colors relative">
                  <div className="absolute left-1 top-0.5 w-4 h-4 bg-white rounded-full shadow-md peer-checked:left-5 transition-all" />
                </div>
                <span className="text-[11px] text-yellow-700 font-semibold transition-colors">
                  Day
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none w-[130px] h-8 bg-white border border-red-200 rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-red-400 justify-center">
                <input
                  type="checkbox"
                  checked={filtroTarefasAtrasadas}
                  onChange={() => setFiltroTarefasAtrasadas((v) => !v)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-red-400 transition-colors relative">
                  <div className="absolute left-1 top-0.5 w-4 h-4 bg-white rounded-full shadow-md peer-checked:left-5 transition-all" />
                </div>
                <span className="text-[11px] text-red-700 font-semibold transition-colors">
                  Atraso
                </span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer select-none w-[130px] h-8 bg-white border border-orange-200 rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-orange-400 justify-center">
                <input
                  type="checkbox"
                  checked={filtroSemTarefa}
                  onChange={() => setFiltroSemTarefa((v) => !v)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-orange-400 transition-colors relative">
                  <div className="absolute left-1 top-0.5 w-4 h-4 bg-white rounded-full shadow-md peer-checked:left-5 transition-all" />
                </div>
                <span className="text-[11px] text-orange-700 font-semibold transition-colors">
                  Sem Tarefa
                </span>
              </label>

              <label
                className="flex items-center gap-2 cursor-pointer select-none w-[150px] h-8 bg-white border border-emerald-200 rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-emerald-400 justify-center px-1"
                title="Mostra leads onde o Total/Orçamento está zerado ou menor que o Fechado."
              >
                <input
                  type="checkbox"
                  checked={filtroOrcamentoSemOportunidade}
                  onChange={() => setFiltroOrcamentoSemOportunidade((v) => !v)}
                  className="sr-only peer"
                />
                <div className="w-10 h-5 bg-gray-300 rounded-full peer-checked:bg-emerald-500 transition-colors relative shrink-0">
                  <div className="absolute left-1 top-0.5 w-4 h-4 bg-white rounded-full shadow-md peer-checked:left-5 transition-all" />
                </div>
                <span className="text-[10px] text-emerald-700 font-semibold transition-colors leading-tight">
                  Total x Fechado
                </span>
              </label>

              <button type="button" onClick={() => setCentralDisparosAberta(true)}
                className="flex h-8 min-w-[188px] items-center justify-center gap-2 rounded-xl bg-blue-800 px-3 text-[11px] font-bold text-white shadow hover:bg-blue-900"
                title="Conferir e preparar WhatsApp para pacientes agendados">
                <FaWhatsapp size={14} /> Conferir disparos ({leadsSelecionadasIds.size})
              </button>

              <LeadsExportExcel
                rows={rows}
                columns={columns}
                procedimentos={procedimentos}
                dentistas={dentistas}
                regioes={regioes}
                usuarios={usuarios}
                fontes={fontes}
                campanhas={campanhas}
                selectedLeadIds={leadsSelecionadasIds}
              />

              {totalLeadsSelecionadasNoFiltro > 0 && (
                <button
                  type="button"
                  onClick={() => setLeadsSelecionadasIds(new Set())}
                  className="flex items-center justify-center gap-1 min-w-[150px] h-8 bg-sky-50 hover:bg-sky-100 text-sky-800 border-2 border-sky-400 rounded-xl text-[11px] shadow-md font-bold transition-all duration-200 px-3"
                  title="Limpar todas as leads selecionadas"
                >
                  ☑️ {totalLeadsSelecionadasNoFiltro} selecionada(s) ×
                </button>
              )}

              <span className="flex items-center justify-center gap-1 min-w-[170px] h-8 bg-white border border-blue-200 text-blue-700 font-semibold rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-blue-400 select-none text-[11px] px-3">
                <span className="mr-1 text-[11px]">💰</span>
                Total: {moeda(resumoFinanceiro.total)}
              </span>

              <span className="flex items-center justify-center gap-1 min-w-[170px] h-8 bg-white border border-green-200 text-green-700 font-semibold rounded-xl shadow transition-all duration-150 hover:shadow-lg hover:border-green-400 select-none text-[11px] px-3">
                <span className="mr-1 text-[11px]">✅</span>
                Fechado: {moeda(resumoFinanceiro.fechado)}
              </span>
            </div>
          </div>

          <div
            ref={tableScrollRef}
            onScroll={handleTableScroll}
            className="w-full max-h-[600px] mt-4 overflow-auto border rounded shadow-inner scrollbar-thin scrollbar-thumb-blue-600 scrollbar-track-blue-100"
          >
            <table
              id="tabela-leads-v3"
              {...getTableProps()}
              className="min-w-[1200px] table-auto border-collapse"
            >
              <thead className="sticky top-0 z-30 shadow-xl bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500 rounded-t-xl overflow-hidden">
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column, index) => (
                      <th
                        {...column.getHeaderProps(
                          column.getSortByToggleProps(),
                        )}
                        className={`text-center font-semibold px-4 py-3 text-sm uppercase tracking-wider text-white font-sans ${
                          index === 0
                            ? "sticky left-0 z-40 shadow-md border-r border-blue-400 backdrop-blur-sm"
                            : ""
                        } ${
                          index === 1
                            ? "sticky left-[45px] z-30 shadow-sm border-r border-blue-400 backdrop-blur-sm"
                            : ""
                        }`}
                      >
                        <div className="flex justify-center items-center gap-1 select-none">
                          {column.render("Header")}
                          {column.isSorted && (
                            <span className="text-white text-xs">
                              {column.isSortedDesc ? "↓" : "↑"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>

              <tbody
                ref={tbodyRef}
                {...getTableBodyProps()}
                className="bg-white"
              >
                {virtualEnabled && visibleWindow.topSpacer > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={
                        headerGroups?.[0]?.headers?.length || columns.length
                      }
                      style={{
                        height: `${visibleWindow.topSpacer}px`,
                        padding: 0,
                        border: 0,
                      }}
                    />
                  </tr>
                )}

                {visibleWindow.visibleRows.map((row) => {
                  prepareRow(row);

                  const leadDaLinha = row.original;
                  const linhaManutencaoAparelho =
                    isLeadManutencaoAparelho(leadDaLinha);

                  return (
                    <tr
                      {...row.getRowProps()}
                      className="transition hover:bg-yellow-100"
                      style={
                        virtualEnabled
                          ? { height: `${VIRTUAL_ROW_HEIGHT}px` }
                          : undefined
                      }
                    >
                      {row.cells.map((cell) => (
                        <td
                          {...cell.getCellProps()}
                          className={`px-4 py-2 border-b text-sm text-gray-800 whitespace-nowrap ${
                            cell.column.id === "id_procedimento" &&
                            linhaManutencaoAparelho
                              ? "bg-violet-100 border-violet-300"
                              : "border-slate-200"
                          } ${
                            cell.column.id === "bolinha_status"
                              ? "sticky left-0 z-20 shadow-md bg-white"
                              : ""
                          } ${
                            cell.column.id === "nome_lead"
                              ? "sticky left-10 z-10 shadow border-r bg-white border-slate-300"
                              : ""
                          }`}
                        >
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}

                {virtualEnabled && visibleWindow.bottomSpacer > 0 && (
                  <tr aria-hidden="true">
                    <td
                      colSpan={
                        headerGroups?.[0]?.headers?.length || columns.length
                      }
                      style={{
                        height: `${visibleWindow.bottomSpacer}px`,
                        padding: 0,
                        border: 0,
                      }}
                    />
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        )}
      </div>

      {modalDataFechamentoAberto && leadDataFechamentoSelecionada && (
        <div className="fixed inset-0 z-[10020] flex items-center justify-center bg-black/45 px-4">
          <div className="w-full max-w-xl overflow-hidden rounded-2xl border border-violet-200 bg-white shadow-2xl">
            <div className="flex items-start justify-between gap-4 bg-gradient-to-r from-violet-800 via-purple-700 to-blue-700 px-5 py-4 text-white">
              <div>
                <h2 className="text-lg font-bold">Data do fechamento</h2>
                <p className="mt-1 text-xs text-violet-100">
                  {leadDataFechamentoSelecionada?.nome_lead || "Paciente"} · {leadDataFechamentoSelecionada?.status_negociacao || "—"}
                </p>
              </div>
              <button
                type="button"
                onClick={fecharModalDataFechamento}
                disabled={salvandoDataFechamento}
                className="shrink-0 rounded-full p-1 hover:bg-white/15 disabled:opacity-60"
                title="Fechar"
              >
                <XCircle className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-5">
              {!leadDataFechamentoSelecionada?.data_fechamento ? (
                <div className="rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-900">
                  Esta é uma venda antiga sem data registrada. Informe o dia real em que o contrato foi fechado.
                </div>
              ) : (
                <div className="rounded-xl border border-blue-200 bg-blue-50 px-3 py-2.5 text-xs text-blue-900">
                  A data já existe. Alterações ficam registradas no histórico de fechamento.
                </div>
              )}

              <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                <label className="text-sm font-semibold text-slate-700">
                  Data do fechamento *
                  <input
                    type="date"
                    value={dataFechamentoManual}
                    max={hojeYmdLocal()}
                    onChange={(e) => setDataFechamentoManual(e.target.value)}
                    disabled={salvandoDataFechamento}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:bg-slate-100"
                  />
                </label>

                <label className="text-sm font-semibold text-slate-700">
                  Quem fechou <span className="font-normal text-slate-400">(opcional)</span>
                  <select
                    value={fechadoPorManual}
                    onChange={(e) => setFechadoPorManual(e.target.value)}
                    disabled={salvandoDataFechamento}
                    className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-white px-3 text-sm font-normal text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:bg-slate-100"
                  >
                    <option value="">Não informado</option>
                    {(usuarios || []).map((usuario) => (
                      <option key={usuario.id} value={usuario.id}>
                        {usuario.nome}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              {leadDataFechamentoSelecionada?.data_fechamento &&
                String(leadDataFechamentoSelecionada.data_fechamento).slice(0, 10) !==
                  dataFechamentoManual && (
                  <label className="block text-sm font-semibold text-slate-700">
                    Motivo da correção *
                    <textarea
                      rows={3}
                      value={motivoDataFechamento}
                      onChange={(e) => setMotivoDataFechamento(e.target.value)}
                      disabled={salvandoDataFechamento}
                      placeholder="Explique por que a data anterior estava incorreta..."
                      className="mt-1 w-full resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm font-normal text-slate-800 outline-none focus:border-violet-500 focus:ring-2 focus:ring-violet-100 disabled:bg-slate-100"
                    />
                    <span className="mt-1 block text-[10px] font-normal text-slate-400">
                      Mínimo de 10 caracteres para correções.
                    </span>
                  </label>
                )}

              <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-[11px] leading-relaxed text-slate-600">
                Esta data será usada como referência comercial para Fechado, Fechado Parcial e Sorriso Planejado. Se houver contrato do Sorriso Planejado, a mesma data será sincronizada nele.
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4">
              <button
                type="button"
                onClick={fecharModalDataFechamento}
                disabled={salvandoDataFechamento}
                className="rounded-xl border border-slate-300 px-4 py-2 font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={salvarDataFechamento}
                disabled={salvandoDataFechamento || !dataFechamentoManual}
                className="inline-flex items-center gap-2 rounded-xl bg-violet-700 px-4 py-2 font-semibold text-white shadow-sm hover:bg-violet-800 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <CalendarCheck className="h-4 w-4" />
                {salvandoDataFechamento ? "Salvando..." : "Salvar fechamento"}
              </button>
            </div>
          </div>
        </div>
      )}

      {modalObservacaoAberto && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-2xl rounded-2xl bg-white shadow-2xl border border-slate-200 overflow-hidden">
            <div className="flex items-start justify-between gap-4 px-5 py-4 bg-gradient-to-r from-blue-900 via-blue-700 to-blue-500 text-white">
              <div>
                <h2 className="text-lg font-bold">Observação do paciente</h2>
                <p className="text-xs text-blue-100 mt-1">
                  Use este campo para dúvidas rápidas, responsáveis, telefones
                  adicionais e orientações internas.
                </p>
              </div>

              <button
                type="button"
                onClick={fecharModalObservacaoPaciente}
                disabled={salvandoObservacaoPaciente}
                className="shrink-0 rounded-full p-1 hover:bg-white/15 disabled:opacity-60"
                title="Fechar"
              >
                <XCircle className="w-6 h-6" />
              </button>
            </div>

            <div className="px-5 py-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                    Paciente
                  </div>
                  <div className="font-semibold text-slate-800 truncate">
                    {leadObservacaoSelecionada?.nome_lead || "—"}
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wide font-semibold text-slate-500">
                    Telefone principal
                  </div>
                  <div className="font-semibold text-slate-800">
                    {leadObservacaoSelecionada?.telefone1 || "—"}
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-2">
                  Observação
                </label>
                <textarea
                  value={textoObservacaoPaciente}
                  onChange={(e) => setTextoObservacaoPaciente(e.target.value)}
                  rows={9}
                  disabled={salvandoObservacaoPaciente}
                  placeholder="Digite aqui observações internas do paciente: responsáveis, telefones adicionais, preferências, cobranças, dúvidas ou qualquer alerta importante..."
                  className="w-full resize-y rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-slate-100"
                />
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-5 py-4 bg-slate-50 border-t border-slate-200">
              <button
                type="button"
                onClick={fecharModalObservacaoPaciente}
                disabled={salvandoObservacaoPaciente}
                className="px-4 py-2 rounded-xl border border-slate-300 text-slate-700 font-semibold hover:bg-slate-100 disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                type="button"
                onClick={salvarObservacaoPaciente}
                disabled={salvandoObservacaoPaciente}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-blue-700 text-white font-semibold hover:bg-blue-800 disabled:opacity-60 shadow-sm"
              >
                <Save className="w-4 h-4" />
                {salvandoObservacaoPaciente
                  ? "Salvando..."
                  : "Salvar observação"}
              </button>
            </div>
          </div>
        </div>
      )}

      {mostrarLogsModal && (
        <LogsModalLead
          lead={leadSelecionadaLogs}
          onClose={() => setMostrarLogsModal(false)}
          tarefas={tarefas}
          interacoesDaLead={interacoesDaLead}
          usuarios={usuarios}
        />
      )}

      <ModalLegendaBolinhas
        isOpen={mostrarLegenda}
        onClose={() => setMostrarLegenda(false)}
      />

      <TarefaModal
        isOpen={modalAberto}
        onClose={async () => {
          setModalAberto(false);
          await finalizarMarcacaoTarefaSorriso();
          refreshLeads();
          await Promise.all([carregarTarefas(), carregarInteracoes()]);
        }}
        lead={leadSelecionada}
        onSalvarTarefa={() => {
          refreshLeads();
          carregarTarefas();
        }}
      />

      <RecebimentoModal
        isOpen={modalRecebimentoAberto && Boolean(leadSelecionada?.id)}
        onClose={() => {
          setModalRecebimentoAberto(false);
          setLeadSelecionada(null);
        }}
        lead={leadSelecionada}
        onRecebimentoSalvo={async () => {
          await Promise.all([refreshLeads(), fetchRecebimentos()]);
        }}
      />

      <InteracoesModal
        isOpen={interacoesModalAberto}
        onClose={async () => {
          setInteracoesModalAberto(false);
          await finalizarMarcacaoInteracaoSorriso();
          await carregarInteracoes();
        }}
        lead={leadSelecionadaParaInteracao}
        tarefas={tarefas}
        onChangeInteracoes={(novasInteracoesDaLead) =>
          setInteracoes((prev) => [
            ...prev.filter(
              (i) =>
                String(i.id_lead ?? i.lead_id) !==
                String(leadSelecionadaParaInteracao?.id),
            ),
            ...novasInteracoesDaLead,
          ])
        }
      />

      <AgendamentosModal
        isOpen={modalAgendamentoAberto}
        onClose={() => setModalAgendamentoAberto(false)}
        lead={agendamentoSelecionado}
        onUltimoAgendamento={(ultimo) =>
          atualizarLeadNaTabela(agendamentoSelecionado?.id, ultimo)
        }
        onAgendamentoSalvo={refreshLeads}
      />

      <TransferenciaLeadModal
        isOpen={modalTransferenciaAberto}
        onClose={() => setModalTransferenciaAberto(false)}
        lead={leadSelecionadaTransferencia}
        usuarios={usuarios}
        onTransferido={async () => {
          const leadId = leadSelecionadaTransferencia?.id;

          if (leadId) {
            const pendenciaLocal = {
              id: `local-${leadId}`,
              lead_id: leadId,
              status: "pendente",
              empresa_id: empresaAtiva?.id ?? usuarioLogado?.empresa_id ?? null,
            };

            setSolicitacoesTransferenciaPendentes((prev) => {
              const jaExiste = (prev || []).some(
                (s) =>
                  String(s?.lead_id || "") === String(leadId) &&
                  String(s?.status || "")
                    .trim()
                    .toLowerCase() === "pendente",
              );

              if (jaExiste) return prev;
              return [pendenciaLocal, ...(prev || [])];
            });

            setLeads((prev) =>
              (prev || []).map((l) =>
                String(l.id) === String(leadId)
                  ? {
                      ...l,
                      _solicitacao_transferencia_pendente: pendenciaLocal,
                    }
                  : l,
              ),
            );

            setTodasLeads((prev) =>
              (prev || []).map((l) =>
                String(l.id) === String(leadId)
                  ? {
                      ...l,
                      _solicitacao_transferencia_pendente: pendenciaLocal,
                    }
                  : l,
              ),
            );
          }

          await refreshLeads();
          await fetchTodasLeads();
          await fetchSolicitacoesTransferenciaPendentes();
          setModalTransferenciaAberto(false);
        }}
      />

      {leadChatAberto && empresaAtiva?.id && (
        <LeadsWhatsApp
          lead={leadChatAberto}
          empresaId={empresaAtiva.id}
          onClose={() => setLeadChatAberto(null)}
          usuarios={usuarios}
          dentistas={dentistas}
          procedimentos={procedimentos}
          agendamentos={agendamentos}
        />
      )}
    </>
  );
}
